/**
 * Vista: Detalle/Nueva Entrega de Tractor
 * Formato: AMX-VTA-FR-07_R8
 */

import ventasService from '../services/ventas.service.js';
import tractorService from '../services/tractor.service.js';
import personaService from '../services/persona.service.js';
import recepcionService from '../services/recepcion.service.js';
import api from '../services/api.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';
import PrintEntrega from './PrintEntrega.js';
import { compressImage } from '../utils/imageCompressor.js';

import { hoyLocal } from '../utils/fechas.js';
import { consultarDanos, avisoDanosHtml } from '../utils/avisoDanos.js';
// =====================================================
// CONSTANTES DEL FORMATO R8
// =====================================================

const ITEMS_INSPECCION_FISICA = [
  'Contrapesos delanteros', 'Llantas delanteras', 'Escape', 'Mofle',
  'Batería', 'Calcas', 'Salpicaderas frontales', 'Llantas traseras',
  'Contrapesos traseros', 'Estabilizadores', 'Barra de tiro', 'Tirón cañero',
  'Barra de tercer punto', 'Cubierta de PTO', 'Torreta', 'Llave de encendido',
  'Asiento', 'Perillas de palancas', 'Acelerador de mano', 'Tablero',
  'Claxon', 'Luces diurnas', 'Luces altas y bajas', 'Direccionales',
  'Luces de trabajo', 'Luces de frenos', 'Emblema (volante, cofre)',
  'Espejos', 'Pintura', 'Carrocería', 'ROPS/Toldo', 'Puntos de engrase',
  'Póliza de garantía', 'Certificado de venta', 'Carnet de garantía',
  'Explicación de uso correcto de marca OCIMA', 'Caja de herramientas',
  'Souvenir (Caja de obsequios AGROMX)'
];

const SISTEMAS_FUNCIONAMIENTO = [
  'Levante', 'Transmisión', 'Tracción delantera', 'Motor',
  'Dirección', 'PTO', 'Eléctrico'
];

const ITEMS_CABINA = [
  'A/Acondicionado', 'Radio', 'Sistema de sonido',
  'Puertas y ventanas', 'Limpiadores', 'Cortina'
];

const ITEMS_INDICACIONES_MANUAL = [
  'Indicaciones generales (Atención)',
  'Tabla de aceites, lubricantes y refrigerantes',
  'Sistema de transmisión',
  'Sistema de enfriamiento',
  'Instrucciones de servicio de filtro de aire',
  'Servicios de mantenimiento obligatorio'
];

const TIPOS_FLUIDOS = [
  { key: 'combustible', display: 'Nivel de Combustible' },
  { key: 'aceite_sistema_hidraulico', display: 'Nivel de Aceite de Sistema Hidráulico' },
  { key: 'aceite_motor', display: 'Nivel de Aceite de Motor' },
  { key: 'aceite_transmision', display: 'Nivel de Aceite de Transmisión' },
  { key: 'aceite_sistema_direccion', display: 'Nivel de Aceite de Sistema de Dirección' },
  { key: 'anticongelante', display: 'Nivel de Anticongelante' },
  { key: 'aceite_frenos', display: 'Nivel de Aceite de Frenos' }
];

const SEGMENTO_A_NIVEL = { 1: 'min', 2: 'bajo', 3: 'medio_bajo', 4: 'medio', 5: 'medio_alto', 6: 'alto', 7: 'max' };
const NIVEL_A_SEGMENTO = { 'min': 1, 'bajo': 2, 'medio_bajo': 3, 'medio': 4, 'medio_alto': 5, 'alto': 6, 'max': 7 };

const FLUIDOS_COLORES = {
  combustible:               { 1:'red', 4:'yellow', 7:'green' },
  aceite_sistema_hidraulico: { 1:'red', 4:'green', 5:'green', 7:'red' },
  aceite_motor:              { 1:'red', 4:'green', 5:'green', 7:'red' },
  aceite_transmision:        { 1:'red', 4:'green', 5:'green', 7:'red' },
  aceite_sistema_direccion:  { 1:'red', 4:'green', 5:'green', 7:'red' },
  anticongelante:            { 1:'red', 4:'green', 5:'green', 7:'red' },
  aceite_frenos:             { 1:'red', 4:'green', 5:'green', 7:'red' }
};

export default {
  data: {
    entrega: null,
    tractor: null,
    isNew: false,
    clientes: [],
    distribuidores: [],
    vendedores: [],
    mode: 'view' // 'new', 'view', 'edit'
  },

  async render(params) {
    this.data.isNew = !params.id || params.id === 'nueva';

    return `
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center;">
          <h2 class="card-title">${this.data.isNew ? 'Nueva Entrega de Tractor' : 'Entrega de Tractor'}</h2>
          <div id="headerActions" style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" onclick="router.navigate('/ventas/entregas')">
              &larr; Volver
            </button>
          </div>
        </div>

        <div id="entregaContent">
          <div style="text-align: center; padding: 2rem;">
            Cargando...
          </div>
        </div>
      </div>
    `;
  },

  async afterRender(params) {
    window.entregaDetalleView = this;

    // Inyectar estilos responsive
    if (!document.getElementById('entrega-detalle-styles')) {
      const style = document.createElement('style');
      style.id = 'entrega-detalle-styles';
      style.textContent = `
        @media (max-width: 640px) {
          .ent-grid-2col { grid-template-columns: 1fr !important; }
        }
      `;
      document.head.appendChild(style);
    }

    // Resetear estado de sesiones anteriores
    this.data.entrega = null;
    this.data.tractor = null;
    this.data.mode = 'view';
    this._newData = null;
    this._editData = null;
    this._fluidosNiveles = null;
    this._toggleSnapshot = null;
    // Buffers diferidos de fotos: sin este reset, una foto marcada para borrar en
    // una entrega editada y abandonada sin guardar se eliminaría de GCS al guardar
    // OTRA entrega (imagen rota en la primera). Ver docs/CHANGELOG_KAREN.md [2026-07-17].
    this._fotosPendientes = [];
    this._fotosEliminar = [];

    // Inyectar estilos de tabs
    if (!document.getElementById('entrega-tab-styles')) {
      const style = document.createElement('style');
      style.id = 'entrega-tab-styles';
      style.textContent = `
        .tab-btn {
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;
          padding: 0.3rem 0.6rem;
          font-size: 0.8rem;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .tab-btn:hover {
          color: var(--primary);
        }
        .tab-btn.active {
          color: var(--primary);
          font-weight: 600;
          border-bottom-color: var(--primary);
          background: transparent;
        }
      `;
      document.head.appendChild(style);
    }

    if (this.data.isNew) {
      this.data.mode = 'new';
      await Promise.all([this.cargarClientes(), this.cargarDistribuidores()]);
      this.renderFormularioNuevo();
    } else {
      await this.cargarEntrega(params.id);
    }
  },

  async cargarClientes() {
    try {
      const response = await personaService.listByRol('cliente');
      this.data.clientes = response.personas || response || [];
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      this.data.clientes = [];
    }
  },

  async cargarDistribuidores() {
    // ERP F1 (D5): sólo personas con rol distribuidor (nunca un empleado) o «Venta directa AgroMX».
    // Antes salían todos los usuarios con persona y así quedó un empleado como distribuidor.
    try {
      const response = await api.get('/personas-erp/selector/distribuidores');
      this.data.distribuidores = (response.data?.personas || []).map(p => ({ id: p.id, nombre_completo: p.nombre_completo }));
    } catch (error) {
      console.error('Error al cargar distribuidores:', error);
      this.data.distribuidores = [];
    }
    // Vendedor (opcional): personas con rol agente de ventas
    try {
      const resp = await api.get('/personas-erp/selector/agentes');
      this.data.vendedores = (resp.data?.personas || []).map(p => ({ id: p.id, nombre_completo: p.nombre_completo }));
    } catch (error) {
      this.data.vendedores = [];
    }
  },

  async cargarEntrega(id) {
    try {
      const response = await ventasService.obtenerEntrega(id);
      this.data.entrega = response.data || response;

      // Resetear signaturePads previos para evitar re-subir firmas ya persistidas
      this.signaturePadDistribuidor = null;
      this.signaturePadUsuario = null;

      if (this.data.entrega.formato === 'R8') {
        if (this.data.entrega.completada) {
          this.data.mode = 'view';
          this.renderEntregaR8();
        } else {
          await this.activarEdicion();
        }
      } else {
        this.renderEntregaLegacy();
      }
    } catch (error) {
      toast.error('Error al cargar entrega');
      console.error(error);
      router.navigate('/ventas/entregas');
    }
  },

  // =====================================================
  // TABS
  // =====================================================

  _renderTabBar(tabs, activeTab) {
    // Conteo de marcas para el badge del tab "Inspección Visual".
    // Fuentes posibles según modo: _editData (new/edit) o data.entrega (view).
    const marcas = this._editData?.marcas_visuales || this.data?.entrega?.marcas_visuales || [];
    const totalMarcas = Array.isArray(marcas) ? marcas.length : 0;

    return `
      <div style="display: flex; gap: 0.5rem; margin-bottom: 1rem; border-bottom: 2px solid var(--bg-secondary); padding-bottom: 0.5rem; flex-wrap: wrap;">
        ${tabs.map(t => {
          // Span siempre presente con id estable para que _updateEdHeader lo
          // refresque al crear/eliminar marcas sin re-renderizar todo el tab bar.
          const badge = t.key === 'inspeccion-visual'
            ? `<span id="entregaTabInspBadge" style="font-size: 0.8em; opacity: 0.75; font-weight: 500; margin-left: 0.15rem;">${totalMarcas > 0 ? ` (${totalMarcas})` : ''}</span>`
            : '';
          return `
            <button type="button" class="btn btn-sm tab-btn ${t.key === activeTab ? 'active' : ''}"
              onclick="window.entregaDetalleView.showTab('${t.key}')">
              ${t.label}${badge}
            </button>
          `;
        }).join('')}
      </div>
    `;
  },

  showTab(tab) {
    const mode = this.data.mode;

    // Guardar datos del tab actual antes de cambiar
    if (mode === 'new') this._collectCurrentTabData();
    if (mode === 'edit') this._collectEditTabData();

    document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
    const tabButton = document.querySelector(`.tab-btn[onclick*="'${tab}'"]`);
    if (tabButton) tabButton.classList.add('active');

    const content = document.getElementById('tabContent');
    if (!content) return;

    if (mode === 'new') {
      this._renderNewTab(tab, content);
    } else if (mode === 'edit') {
      this._renderEditTab(tab, content);
    } else {
      this._renderViewTab(tab, content);
    }

    // Sync checkboxes con su toggle "Seleccionar todo"
    this._attachCheckboxSync(content);
    this._syncAllToggles(content);

    // Si el tab es firmas en modo edición, inicializar SignaturePad con delay
    if (tab === 'firmas' && mode === 'edit') {
      requestAnimationFrame(() => {
        setTimeout(() => this.loadSignaturePad(), 150);
      });
    }
  },

  // =====================================================
  // FORMULARIO NUEVA ENTREGA (R8)
  // =====================================================

  renderFormularioNuevo() {
    const container = document.getElementById('entregaContent');
    this._fluidosNiveles = {};

    const tabs = [
      { key: 'inspeccion', label: 'Inspección' },
      { key: 'fluidos', label: 'Niveles y Tapones' },
      { key: 'observaciones', label: 'Observaciones' }
    ];

    container.innerHTML = `
      <form id="formEntrega" novalidate>
        <!-- Buscar Tractor -->
        <div style="border: 1px solid var(--gray-200); padding: 1.25rem; border-radius: 8px; margin-bottom: 1.5rem; background: var(--bg-secondary); box-shadow: 0 1px 3px rgba(0,0,0,0.08);">
          <div style="margin-bottom: 1rem;">
            <h4 style="margin: 0 0 0.25rem; font-size: 0.95rem; font-weight: 600;">Buscar por número de serie</h4>
            <p style="margin: 0; font-size: 0.8rem; color: var(--gray-500);">Ingrese el número de serie del tractor para verificar si puede entregarse y cargar sus datos automáticamente</p>
          </div>
          <div style="display: flex; gap: 0.75rem; align-items: center;">
            <input type="text" id="numSerieBuscar" class="form-input" placeholder="Ej: TB504-2025-003" style="flex: 1;" />
            <button type="button" class="btn btn-primary" onclick="window.entregaDetalleView.buscarTractor()">Buscar</button>
          </div>
          <div id="tractorResult" style="margin-top: 1rem;"></div>
        </div>

        <!-- Formulario principal (oculto hasta buscar tractor) -->
        <div id="formularioPrincipal" style="display: none;">
          <input type="hidden" name="num_serie" id="numSerie" />

          <!-- Datos del Tractor -->
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
                <input type="text" id="tractorMarca" class="form-input" readonly style="background: var(--gray-50);" value="" />
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
                <input type="text" id="tractorModelo" class="form-input" readonly style="background: var(--gray-50);" value="" />
              </div>
            </div>
          </div>

          <!-- Datos de Entrega -->
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos de Entrega<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha *</label>
                <input type="date" name="fecha" class="form-input" value="${hoyLocal()}" required />
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Horas Motor *</label>
                <input type="number" name="horas_motor" id="horasMotor" class="form-input" step="0.01" min="0" required />
              </div>
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Cliente *</label>
                <select name="cliente_id" id="clienteSelect" class="form-select" required>
                  <option value="">Seleccione un cliente...</option>
                  ${(this.data.clientes || []).map(c => `
                    <option value="${c.id}" data-nombre="${c.nombre_completo}">
                      ${c.nombre_completo}${c.rfc ? ` - ${c.rfc}` : ''}
                    </option>
                  `).join('')}
                </select>
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Distribuidor *</label>
                <select name="distribuidor_id" id="distribuidorSelect" class="form-select" required>
                  <option value="">Seleccione...</option>
                  ${(this.data.distribuidores || []).map(d => `
                    <option value="${d.id}" data-nombre="${d.nombre_completo}">
                      ${d.nombre_completo}
                    </option>
                  `).join('')}
                </select>
              </div>
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Vendedor (opcional)</label>
                <select name="vendedor_persona_id" id="vendedorSelect" class="form-select">
                  <option value="">${(this.data.vendedores || []).length ? '— Sin vendedor —' : '— Aún no hay vendedores dados de alta —'}</option>
                  ${(this.data.vendedores || []).map(v => `<option value="${v.id}">${v.nombre_completo}</option>`).join('')}
                </select>
              </div>
            </div>
          </div>

          <!-- Tabs -->
          ${this._renderTabBar(tabs, 'inspeccion')}
          <div id="tabContent" style="margin-bottom: 1.5rem;"></div>

          <!-- Botones -->
          <div style="display: flex; gap: 1rem; justify-content: flex-end;">
            <button type="button" class="btn btn-secondary" onclick="router.navigate('/ventas/entregas')">
              Cancelar
            </button>
            <button type="submit" class="btn btn-success">
              Crear Entrega
            </button>
          </div>
        </div>
      </form>
    `;

    // Renderizar primer tab
    const tabContent = document.getElementById('tabContent');
    this._renderNewTab('inspeccion', tabContent);
    this._attachCheckboxSync(tabContent);
    this._syncAllToggles(tabContent);

    document.getElementById('formEntrega').addEventListener('submit', (e) => this.guardarNueva(e));

    // Enter en búsqueda dispara buscar; si edita el NS tras búsqueda OK, resetea formulario
    const nsBuscar = document.getElementById('numSerieBuscar');
    if (nsBuscar) {
      nsBuscar.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.buscarTractor();
        }
      });
      nsBuscar.addEventListener('input', () => {
        if (this.data.tractor) {
          this.data.tractor = null;
          const fp = document.getElementById('formularioPrincipal');
          if (fp) fp.style.display = 'none';
          const tr = document.getElementById('tractorResult');
          if (tr) tr.innerHTML = '';
        }
      });
    }
  },

  _renderAccordionSection(key, title, items, namePrefix, data) {
    const checked = Object.values(data || {}).filter(v => v).length;
    const total = items.length;
    const isComplete = checked === total && total > 0;
    const inProgress = checked > 0 && !isComplete;

    let badgeText, badgeBg, badgeColor, iconBg, iconColor;
    if (isComplete) {
      badgeText = 'Completo'; badgeBg = 'rgba(16,185,129,0.1)'; badgeColor = 'var(--success)';
      iconBg = 'var(--success)'; iconColor = 'white';
    } else if (inProgress) {
      badgeText = 'En progreso'; badgeBg = 'rgba(245,158,11,0.1)'; badgeColor = '#f59e0b';
      iconBg = '#f59e0b'; iconColor = 'white';
    } else {
      badgeText = 'Pendiente'; badgeBg = 'var(--gray-100)'; badgeColor = 'var(--gray-500)';
      iconBg = 'var(--gray-200)'; iconColor = 'var(--gray-500)';
    }

    return `
      <div style="border:1px solid var(--gray-200);border-radius:12px;margin-bottom:0.75rem;overflow:hidden;">
        <div style="display:flex;align-items:center;padding:0.85rem 1rem;cursor:pointer;gap:0.75rem;" onclick="window.entregaDetalleView._toggleEntregaSeccion('${key}')">
          <div id="entSecIcon-${key}" style="width:32px;height:32px;border-radius:50%;background:${iconBg};color:${iconColor};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <span style="font-size:0.8rem;">&#10003;</span>
          </div>
          <div style="flex:1;">
            <div style="font-size:0.9rem;font-weight:600;color:var(--gray-800);">${title}</div>
            <div id="entSecCount-${key}" style="font-size:0.75rem;color:var(--gray-500);">${checked} / ${total} respondidos</div>
          </div>
          <span id="entSecBadge-${key}" style="font-size:0.7rem;font-weight:600;padding:0.25rem 0.6rem;border-radius:6px;background:${badgeBg};color:${badgeColor};">${badgeText}</span>
          <span id="entChevron-${key}" style="font-size:0.75rem;color:var(--gray-400);transition:transform 0.2s ease;display:inline-block;">&#9660;</span>
        </div>
        <div id="entSecBody-${key}" style="display:none;border-top:1px solid var(--gray-100);" onclick="event.stopPropagation()">
          <div style="padding:0.5rem 1rem;">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.25rem;">
              ${items.map((item, i) => `
                <label style="display:flex;align-items:center;gap:0.5rem;cursor:pointer;padding:0.4rem 0.5rem;border-radius:4px;" onmouseover="this.style.background='var(--bg-primary)'" onmouseout="this.style.background='transparent'">
                  <input type="checkbox" name="${namePrefix}_${i}" data-section="${namePrefix}" data-accordion="${key}" ${data?.[i] ? 'checked' : ''} onchange="window.entregaDetalleView._updateAccordionCount('${key}', '${namePrefix}', ${total})" />
                  <span style="font-size:0.85rem;">${item}</span>
                </label>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  },

  _toggleEntregaSeccion(key) {
    const body = document.getElementById(`entSecBody-${key}`);
    const chevron = document.getElementById(`entChevron-${key}`);
    if (!body) return;
    const isOpen = body.style.display !== 'none';
    body.style.display = isOpen ? 'none' : 'block';
    if (chevron) chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
  },

  _updateAccordionCount(key, namePrefix, total) {
    const checks = document.querySelectorAll(`input[data-accordion="${key}"]`);
    const checked = Array.from(checks).filter(c => c.checked).length;
    const isComplete = checked === total && total > 0;
    const inProgress = checked > 0 && !isComplete;

    const countEl = document.getElementById(`entSecCount-${key}`);
    if (countEl) countEl.textContent = `${checked} / ${total} respondidos`;

    let badgeText, badgeBg, badgeColor, iconBg, iconColor;
    if (isComplete) {
      badgeText = 'Completo'; badgeBg = 'rgba(16,185,129,0.1)'; badgeColor = 'var(--success)';
      iconBg = 'var(--success)'; iconColor = 'white';
    } else if (inProgress) {
      badgeText = 'En progreso'; badgeBg = 'rgba(245,158,11,0.1)'; badgeColor = '#f59e0b';
      iconBg = '#f59e0b'; iconColor = 'white';
    } else {
      badgeText = 'Pendiente'; badgeBg = 'var(--gray-100)'; badgeColor = 'var(--gray-500)';
      iconBg = 'var(--gray-200)'; iconColor = 'var(--gray-500)';
    }

    const badgeEl = document.getElementById(`entSecBadge-${key}`);
    if (badgeEl) {
      badgeEl.textContent = badgeText;
      badgeEl.style.background = badgeBg;
      badgeEl.style.color = badgeColor;
    }

    const iconEl = document.getElementById(`entSecIcon-${key}`);
    if (iconEl) {
      iconEl.style.background = iconBg;
      iconEl.style.color = iconColor;
    }
  },

  _renderNewTab(tab, container) {
    switch (tab) {
      case 'inspeccion': {
        const d = this._newData || {};
        container.innerHTML = `
          ${this._renderAccordionSection('inspeccion_fisica', 'Inspección Física', ITEMS_INSPECCION_FISICA, 'inspeccion', d.inspeccion)}
          ${this._renderAccordionSection('sistema', 'Funcionamiento de Sistema', SISTEMAS_FUNCIONAMIENTO, 'sistema', d.sistema)}
          ${this._renderAccordionSection('cabina', 'Cabina', ITEMS_CABINA, 'cabina', d.cabina)}
          ${this._renderAccordionSection('indicacion', 'Indicaciones del Manual', ITEMS_INDICACIONES_MANUAL, 'indicacion', d.indicacion)}
        `;
        break;
      }

      case 'fluidos': {
        const d = this._newData || {};
        container.innerHTML = `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
            <div class="nivel-fluidos-grid">
              ${TIPOS_FLUIDOS.map((f, i) => this._renderNivelBar(f.key, f.display, this._fluidosNiveles?.[`new_${f.key}`] || 0, false, `new_${f.key}`)).join('')}
            </div>
          </div>
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Tapones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.25rem;">
                ${TIPOS_FLUIDOS.map((f, i) => {
                  const checked = d.fluido_tapon?.[i] || false;
                  return `
                    <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer; padding: 0.25rem 0.5rem;">
                      <input type="checkbox" name="fluido_tapon_${i}" ${checked ? 'checked' : ''} />
                      <span style="font-size: 0.85rem;">${f.display.replace('Nivel de ', '')}</span>
                    </label>`;
                }).join('')}
              </div>
          </div>
        `;
        break;
      }

      case 'observaciones': {
        container.innerHTML = `
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea name="observaciones" class="form-input" rows="4" placeholder="Observaciones generales...">${this._newData?.observaciones || ''}</textarea>
          </div>
        `;
        break;
      }

    }
  },

  async buscarTractor() {
    const numSerie = document.getElementById('numSerieBuscar').value;
    if (!numSerie) {
      toast.error('Ingrese el número de serie');
      return;
    }

    const resultDiv = document.getElementById('tractorResult');
    resultDiv.innerHTML = '<p>Buscando...</p>';

    try {
      const response = await tractorService.getByNumSerie(numSerie);
      const tractor = response.data || response;
      this.data.tractor = tractor;

      if (tractor.estado !== 'liberado') {
        resultDiv.innerHTML = `
          <div style="padding: 1rem; background: var(--warning); color: white; border-radius: 8px;">
            <strong>Tractor no disponible para entrega</strong>
            <p style="margin-top: 0.5rem;">El tractor debe estar en estado "Liberado" para ser entregado.</p>
            <p>Estado actual: <strong>${tractor.estado || 'Desconocido'}</strong></p>
          </div>
        `;
        document.getElementById('formularioPrincipal').style.display = 'none';
        return;
      }

      // C-323c: con daños abiertos no se entrega (el servidor también lo rechaza). Se avisa antes de capturar.
      // C-323d: con el candado en modo aviso se advierte y se deja seguir (el servidor deja constancia).
      const danos = await consultarDanos(numSerie);
      if (danos && danos.total > 0 && danos.modo !== 'aviso') {
        resultDiv.innerHTML = avisoDanosHtml(danos, { bloquea: true, accion: 'entregar' });
        document.getElementById('formularioPrincipal').style.display = 'none';
        return;
      }

      resultDiv.innerHTML = danos && danos.total > 0 ? avisoDanosHtml(danos, { bloquea: true, accion: 'entregar' }) : '';
      toast.success('Tractor verificado. Puede completar la entrega.');

      document.getElementById('formularioPrincipal').style.display = 'block';
      document.getElementById('numSerie').value = numSerie;
      document.getElementById('horasMotor').value = tractor.horas_motor || 0;

      // Llenar datos del tractor
      document.getElementById('tractorMarca').value = tractor.marca || '-';
      document.getElementById('tractorModelo').value = tractor.modelo || '-';

      if (tractor.cliente && tractor.cliente.id) {
        const clienteSelect = document.getElementById('clienteSelect');
        if (clienteSelect) clienteSelect.value = tractor.cliente.id;
      }

    } catch (error) {
      this.data.tractor = null;
      const msg = (error.message || 'Tractor no encontrado').replace(/ no encontrado/gi, '');
      resultDiv.innerHTML = `
        <div style="padding: 1rem; background: var(--danger); color: white; border-radius: 8px;">
          ${msg} no encontrado
        </div>
      `;
      document.getElementById('formularioPrincipal').style.display = 'none';
      console.error(error);
    }
  },

  async guardarNueva(e) {
    e.preventDefault();

    if (!this.data.tractor) {
      toast.error('Debe buscar y seleccionar un tractor válido primero');
      return;
    }

    const clienteSelect = document.getElementById('clienteSelect');
    const clienteId = clienteSelect?.value;
    const clienteNombre = clienteSelect?.options[clienteSelect.selectedIndex]?.dataset?.nombre || '';

    if (!clienteId) {
      toast.error('Debe seleccionar un cliente');
      return;
    }

    const distribuidorSelect = document.getElementById('distribuidorSelect');
    const distribuidorId = distribuidorSelect?.value || null;
    const distribuidorNombre = distribuidorSelect?.options[distribuidorSelect.selectedIndex]?.dataset?.nombre || '';

    if (!distribuidorId) {
      toast.error('Debe seleccionar un distribuidor');
      return;
    }

    const form = e.target;

    // Recolectar de TODOS los tabs (algunos checkboxes pueden no estar en DOM si el tab no está activo)
    // Usamos this._newData para almacenar datos de tabs previos
    this._collectCurrentTabData();

    const inspeccion_fisica = ITEMS_INSPECCION_FISICA.map((item, i) => ({
      item,
      verificado: !!(this._newData?.inspeccion?.[i])
    }));

    const funcionamiento_sistema = SISTEMAS_FUNCIONAMIENTO.map((sistema, i) => ({
      sistema,
      verificado: !!(this._newData?.sistema?.[i])
    }));

    const cabina = ITEMS_CABINA.map((item, i) => ({
      item,
      verificado: !!(this._newData?.cabina?.[i])
    }));

    const indicaciones_manual = ITEMS_INDICACIONES_MANUAL.map((item, i) => ({
      item,
      verificado: !!(this._newData?.indicacion?.[i])
    }));

    const niveles_fluidos = TIPOS_FLUIDOS.map((f, i) => {
      const segKey = `new_${f.key}`;
      const segmento = (this._fluidosNiveles && this._fluidosNiveles[segKey]) || 0;
      return {
        fluido: f.display,
        nivel: SEGMENTO_A_NIVEL[segmento] || null,
        tapon_verificado: !!(this._newData?.fluido_tapon?.[i])
      };
    });

    const data = {
      num_serie: form.querySelector('[name="num_serie"]')?.value,
      usuario: clienteNombre,
      cliente_id: parseInt(clienteId, 10),
      distribuidor_id: distribuidorId ? parseInt(distribuidorId, 10) : null,
      distribuidor: distribuidorNombre || null,
      vendedor_persona_id: document.getElementById('vendedorSelect')?.value ? parseInt(document.getElementById('vendedorSelect').value, 10) : null,
      fecha: form.querySelector('[name="fecha"]')?.value,
      horas_motor: parseFloat(form.querySelector('[name="horas_motor"]')?.value),
      observaciones: form.querySelector('[name="observaciones"]')?.value || null,
      inspeccion_fisica,
      funcionamiento_sistema,
      cabina,
      indicaciones_manual,
      niveles_fluidos,
      marcas_visuales: this._editData?.marcas_visuales || []
    };

    try {
      const response = await ventasService.crearEntrega(data);
      toast.success('Entrega creada exitosamente');
      const entrega = response.data || response;
      router.navigate(`/ventas/entregas/${entrega.id}`);
    } catch (error) {
      toast.error(error.message || 'Error al crear entrega');
      console.error('Detalles:', error.details);
      console.error(error);
    }
  },

  // Guardar datos del tab actual antes de cambiar
  _collectCurrentTabData() {
    if (!this._newData) this._newData = { inspeccion: {}, sistema: {}, cabina: {}, indicacion: {}, fluido_tapon: {}, observaciones: '' };

    // Inspección
    document.querySelectorAll('[name^="inspeccion_"]').forEach(cb => {
      const idx = parseInt(cb.name.replace('inspeccion_', ''));
      this._newData.inspeccion[idx] = cb.checked;
    });

    // Sistemas
    document.querySelectorAll('[name^="sistema_"]').forEach(cb => {
      const idx = parseInt(cb.name.replace('sistema_', ''));
      this._newData.sistema[idx] = cb.checked;
    });

    // Cabina
    document.querySelectorAll('[name^="cabina_"]').forEach(cb => {
      const idx = parseInt(cb.name.replace('cabina_', ''));
      this._newData.cabina[idx] = cb.checked;
    });

    // Indicaciones
    document.querySelectorAll('[name^="indicacion_"]').forEach(cb => {
      const idx = parseInt(cb.name.replace('indicacion_', ''));
      this._newData.indicacion[idx] = cb.checked;
    });

    // Tapones
    document.querySelectorAll('[name^="fluido_tapon_"]').forEach(cb => {
      const idx = parseInt(cb.name.replace('fluido_tapon_', ''));
      this._newData.fluido_tapon[idx] = cb.checked;
    });

  },

  // =====================================================
  // VISTA ENTREGA EXISTENTE — FORMATO R8 (solo lectura)
  // =====================================================

  renderEntregaR8() {
    const container = document.getElementById('entregaContent');
    const e = this.data.entrega;
    this.data.mode = 'view';

    const tabs = [
      { key: 'inspeccion', label: 'Inspección' },
      { key: 'fluidos', label: 'Niveles y Tapones' },
      { key: 'observaciones', label: 'Observaciones' },
      { key: 'inspeccion-visual', label: 'Inspección Visual' },
    ];
    if (e.completada) {
      tabs.push({ key: 'firmas', label: 'Firmas' });
    }

    container.innerHTML = `
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Num. Serie</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.num_serie}" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.marca || '-'}" />
          </div>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.modelo || '-'}" />
          </div>
        </div>
      </div>

      <!-- Datos de Entrega -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos de Entrega<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${new Date(e.fecha).toLocaleDateString('es-MX')}" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Horas Motor</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.horas_motor} hrs" />
          </div>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Reporte CRM</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.reporte_crm || '-'}" />
          </div>
        </div>
      </div>

      <!-- Tabs -->
      ${this._renderTabBar(tabs, 'inspeccion')}
      <div id="tabContent" style="margin-bottom: 1.5rem;"></div>

    `;

    this._renderViewTab('inspeccion', document.getElementById('tabContent'));

    // Agregar botones de Imprimir y Descargar al header
    if (e.completada) {
      const headerActions = document.getElementById('headerActions');
      if (headerActions) {
        headerActions.innerHTML = `
          <button class="btn btn-secondary" onclick="router.navigate('/ventas/entregas')">
            &larr; Volver
          </button>
          ${permisos.canPrint('entregas') ? `
            <button class="btn btn-primary" onclick="router.navigate('/ventas/entregas/${e.id}/print')">
              🖨️ Imprimir
            </button>
            <button class="btn btn-success" onclick="window.entregaDetalleView.descargarPDF()">
              📄 Descargar PDF
            </button>
          ` : ''}
        `;
      }
    }
  },

  _renderViewTab(tab, container) {
    const e = this.data.entrega;

    switch (tab) {
      case 'inspeccion': {
        const inspeccionData = {};
        (e.inspeccion_fisica || []).forEach((item, i) => { inspeccionData[i] = item.verificado; });
        const sistemaData = {};
        (e.funcionamiento_sistema || []).forEach((item, i) => { sistemaData[i] = item.verificado; });
        const cabinaData = {};
        (e.cabina || []).forEach((item, i) => { cabinaData[i] = item.verificado; });
        const indicacionData = {};
        (e.indicaciones_manual || []).forEach((item, i) => { indicacionData[i] = item.verificado; });

        container.innerHTML = `
          ${this._renderAccordionSection('view_inspeccion', 'Inspección Física', ITEMS_INSPECCION_FISICA, 'view-inspeccion', inspeccionData)}
          ${this._renderAccordionSection('view_sistema', 'Funcionamiento de Sistema', SISTEMAS_FUNCIONAMIENTO, 'view-sistema', sistemaData)}
          ${this._renderAccordionSection('view_cabina', 'Cabina', ITEMS_CABINA, 'view-cabina', cabinaData)}
          ${this._renderAccordionSection('view_indicacion', 'Indicaciones del Manual', ITEMS_INDICACIONES_MANUAL, 'view-indicacion', indicacionData)}
        `;
        // En visualización deshabilitar los checkboxes
        container.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.disabled = true; });
        break;
      }

      case 'fluidos':
        container.innerHTML = `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
            <div class="nivel-fluidos-grid">
              ${(e.niveles_fluidos || []).map(f => {
                const fluidoInfo = TIPOS_FLUIDOS.find(t => t.display === f.fluido) || TIPOS_FLUIDOS[0];
                const segmento = NIVEL_A_SEGMENTO[f.nivel] || 0;
                return this._renderNivelBar(fluidoInfo.key, f.fluido, segmento, true);
              }).join('')}
            </div>
          </div>
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Tapones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.25rem;">
                ${(e.niveles_fluidos || []).map(f => `
                    <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0.5rem; border-radius: 4px;">
                      <input type="checkbox" disabled ${f.tapon_verificado ? 'checked' : ''} />
                      <span style="font-size: 0.85rem;">${(f.fluido || '').replace('Nivel de ', '')}</span>
                    </label>`).join('')}
              </div>
          </div>
        `;
        break;

      case 'observaciones':
        container.innerHTML = `
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea class="form-input" rows="4" readonly style="background: var(--gray-50);">${e.observaciones || ''}</textarea>
          </div>
        `;
        break;

      case 'inspeccion-visual':
        this._injectInspeccionVisualStyles();
        const marcas = Array.isArray(e.marcas_visuales) ? e.marcas_visuales : [];
        container.innerHTML = `
          <div class="iv-card">
            <div class="iv-header-row">
              <button type="button" class="iv-nav-arrow" aria-label="Vista anterior" onclick="window.entregaDetalleView._navViewSlide(-1)">‹</button>
              <div class="iv-view-pill">
                <strong id="evViewTitle">Frontal</strong>
              </div>
              <button type="button" class="iv-nav-arrow" aria-label="Vista siguiente" onclick="window.entregaDetalleView._navViewSlide(1)">›</button>
            </div>
            <p class="iv-mode-hint iv-mode-hint-move">💡 Arrastra la imagen para moverte · Toca un punto marcado para ver su foto</p>
            <div class="iv-image-area" style="position:relative;max-width:1000px;margin:0 auto;">
              <div class="iv-zoom-stack iv-zoom-overlay">
                <button type="button" class="iv-zoom-btn" aria-label="Aumentar zoom" onclick="window.entregaDetalleView._zoomViewActive(1)">+</button>
                <span id="evZoomLabelActive" class="iv-zoom-lbl">100%</span>
                <button type="button" class="iv-zoom-btn" aria-label="Reducir zoom" onclick="window.entregaDetalleView._zoomViewActive(-1)">−</button>
              </div>
              ${[
                { key: 'tractor-frente', alt: 'Parte Frontal', sfx: 'Frente' },
                { key: 'tractor-trasera', alt: 'Parte Trasera', sfx: 'Trasera' },
                { key: 'tractor-lateral-izq', alt: 'Lateral Izquierdo', sfx: 'LatIzq' },
                { key: 'tractor-lateral-der', alt: 'Lateral Derecho', sfx: 'LatDer' }
              ].map((s, i) => `
                <div id="viewSlide-${s.key}" style="display:${i === 0 ? 'block' : 'none'};">
                  <div id="viewScroll${s.sfx}" style="overflow: hidden; border: 1px solid var(--gray-300); border-radius: 8px; background: #fff; text-align: center;">
                    <div style="position: relative; display: inline-block;">
                      <img id="viewImg${s.sfx}" src="/img/${s.key}.jpeg" alt="${s.alt}" style="display: block; width: 100%;" />
                      <canvas id="viewCanvas${s.sfx}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer;"></canvas>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
            <p class="iv-hint-keyboard" style="font-size:0.75rem;color:var(--text-muted);text-align:center;margin-top:0.5rem;">⌨️ También puedes usar las flechas ← → del teclado</p>
          </div>
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea class="form-input" rows="4" readonly style="background: var(--gray-50);">${e.observaciones_visual || ''}</textarea>
          </div>
        `;
        this._zoomLevels = { 'tractor-frente': 1, 'tractor-trasera': 1, 'tractor-lateral-izq': 1, 'tractor-lateral-der': 1 };
        this._viewMarcas = marcas;
        this._activeViewSlide = 'tractor-frente';
        this._viewSlidesOrder = ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
        this._updateViewHeader();
        setTimeout(() => {
          if (marcas.length > 0) {
            this._drawViewMarks('viewCanvasFrente', 'viewImgFrente', 'tractor-frente', marcas);
            this._drawViewMarks('viewCanvasTrasera', 'viewImgTrasera', 'tractor-trasera', marcas);
            this._drawViewMarks('viewCanvasLatIzq', 'viewImgLatIzq', 'tractor-lateral-izq', marcas);
            this._drawViewMarks('viewCanvasLatDer', 'viewImgLatDer', 'tractor-lateral-der', marcas);
            this._addViewClickHandler('viewCanvasFrente', 'tractor-frente', marcas);
            this._addViewClickHandler('viewCanvasTrasera', 'tractor-trasera', marcas);
            this._addViewClickHandler('viewCanvasLatIzq', 'tractor-lateral-izq', marcas);
            this._addViewClickHandler('viewCanvasLatDer', 'tractor-lateral-der', marcas);
          }
          // Drag-scroll con mouse para los 4 slides del modo view (independiente
          // de si hay marcas o no — el usuario igual puede querer ver el detalle
          // de la imagen con zoom).
          [['viewScrollFrente', 'tractor-frente'],
           ['viewScrollTrasera', 'tractor-trasera'],
           ['viewScrollLatIzq', 'tractor-lateral-izq'],
           ['viewScrollLatDer', 'tractor-lateral-der']].forEach(([id, key]) => {
            this._attachScrollPan(document.getElementById(id), () => (this._zoomLevels && this._zoomLevels[key]) || 1);
          });
        }, 100);
        if (!this._evKeydownHandler) {
          this._evKeydownHandler = (ev) => {
            if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
            const t = ev.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            const card = document.querySelector('.iv-card');
            if (!card || card.offsetParent === null) return;
            this._navViewSlide(ev.key === 'ArrowRight' ? 1 : -1);
          };
          document.addEventListener('keydown', this._evKeydownHandler);
        }
        break;

      case 'firmas':
        container.innerHTML = `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2rem;">
              <div style="text-align: center;">
                ${e.firma_distribuidor_url ? `<img src="${e.firma_distribuidor_url}" style="width: 100%; max-height: 80px; object-fit: contain; margin-bottom: 0.5rem;" onerror="this.style.display='none'" />` : '<div style="height: 80px;"></div>'}
                <div style="border-top: 1px solid var(--gray-400); padding-top: 0.5rem;">
                  <strong>${e.distribuidor || '-'}</strong><br>
                  <small style="color: var(--text-muted);">Distribuidor</small>
                </div>
              </div>
              <div style="text-align: center;">
                ${e.firma_usuario_url ? `<img src="${e.firma_usuario_url}" style="width: 100%; max-height: 80px; object-fit: contain; margin-bottom: 0.5rem;" onerror="this.style.display='none'" />` : '<div style="height: 80px;"></div>'}
                <div style="border-top: 1px solid var(--gray-400); padding-top: 0.5rem;">
                  <strong>${e.usuario || '-'}</strong><br>
                  <small style="color: var(--text-muted);">Usuario</small>
                </div>
              </div>
            </div>
          </div>
        `;
        break;
    }
  },

  // =====================================================
  // MODO EDICIÓN (R8)
  // =====================================================

  async activarEdicion() {
    if (!this.data.distribuidores || this.data.distribuidores.length === 0) {
      await this.cargarDistribuidores();
    }

    const container = document.getElementById('entregaContent');
    const e = this.data.entrega;
    this.data.mode = 'edit';

    // Inicializar niveles de fluidos para edición
    this._fluidosNiveles = {};
    const fluidos = e.niveles_fluidos || [];
    TIPOS_FLUIDOS.forEach((fl, i) => {
      const f = fluidos[i] || {};
      const segmento = NIVEL_A_SEGMENTO[f.nivel] || 0;
      if (segmento) this._fluidosNiveles[`edit_fluido_${fl.key}`] = segmento;
    });

    // Inicializar datos editados
    this._editData = {
      inspeccion: {},
      sistema: {},
      cabina: {},
      indicacion: {},
      fluido_tapon: {},
      observaciones: e.observaciones || '',
      observaciones_visual: e.observaciones_visual || '',
      reporte_crm: e.reporte_crm || '',
      marcas_visuales: Array.isArray(e.marcas_visuales) ? [...e.marcas_visuales] : []
    };

    // Prellenar datos editados
    (e.inspeccion_fisica || []).forEach((item, i) => { this._editData.inspeccion[i] = item.verificado; });
    (e.funcionamiento_sistema || []).forEach((item, i) => { this._editData.sistema[i] = item.verificado; });
    (e.cabina || []).forEach((item, i) => { this._editData.cabina[i] = item.verificado; });
    (e.indicaciones_manual || []).forEach((item, i) => { this._editData.indicacion[i] = item.verificado; });
    fluidos.forEach((f, i) => { this._editData.fluido_tapon[i] = f.tapon_verificado; });

    const tabs = [
      { key: 'inspeccion', label: 'Inspección' },
      { key: 'fluidos', label: 'Niveles y Tapones' },
      { key: 'observaciones', label: 'Observaciones' },
      { key: 'inspeccion-visual', label: 'Inspección Visual' },
      { key: 'firmas', label: 'Firmas' }
    ];

    container.innerHTML = `
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Num. Serie</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.num_serie}" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.marca || '-'}" />
          </div>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
            <input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${e.modelo || '-'}" />
          </div>
        </div>
      </div>

      <!-- Datos de Entrega -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos de Entrega<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha</label>
            <input type="date" id="editFecha" class="form-input" value="${e.fecha ? e.fecha.substring(0, 10) : ''}" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Horas Motor</label>
            <input type="number" id="editHorasMotor" class="form-input" step="0.01" min="0" value="${e.horas_motor || 0}" />
          </div>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;" class="ent-grid-2col">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Reporte CRM *</label>
            <input type="text" id="editReporteCRM" class="form-input" value="${this._editData.reporte_crm || ''}" placeholder="Número de reporte CRM" />
          </div>
        </div>
      </div>

      <!-- Tabs -->
      ${this._renderTabBar(tabs, 'inspeccion')}
      <div id="tabContent" style="margin-bottom: 1.5rem;"></div>

      <div id="unsavedIndicator" style="display: none; background: var(--warning); color: white; padding: 0.5rem 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center;">
        Hay cambios sin guardar
      </div>

      <!-- C-323c: aviso de daños abiertos (con daños no se puede completar la entrega) -->
      <div id="avisoDanosEntrega"></div>

      <!-- Botones -->
      <div style="display: flex; gap: 1rem; justify-content: flex-end;">
        <button class="btn btn-primary" onclick="window.entregaDetalleView.guardarEdicion()">
          Guardar Cambios
        </button>
        ${permisos.can('entregas', 'cerrar') ? `
          <button class="btn btn-success" onclick="window.entregaDetalleView.completarEntrega()">
            Completar Entrega
          </button>
        ` : ''}
      </div>
    `;

    consultarDanos(e.num_serie).then((danos) => {
      const caja = document.getElementById('avisoDanosEntrega');
      if (caja) caja.innerHTML = avisoDanosHtml(danos, { bloquea: true, accion: 'completar la entrega de' });
    });

    const editTabContent = document.getElementById('tabContent');
    this._renderEditTab('inspeccion', editTabContent);
    this._attachCheckboxSync(editTabContent);
    this._syncAllToggles(editTabContent);

    // Botones en header para edición
    const headerActions = document.getElementById('headerActions');
    if (headerActions) {
      headerActions.innerHTML = `
        <button class="btn btn-secondary" onclick="router.navigate('/ventas/entregas')">
          &larr; Volver
        </button>
      `;
    }

    // Detectar cambios en fecha, horas motor y reporte CRM
    const editFecha = document.getElementById('editFecha');
    const editHoras = document.getElementById('editHorasMotor');
    const editCRM = document.getElementById('editReporteCRM');
    [editFecha, editHoras, editCRM].forEach(el => {
      if (el) {
        el.addEventListener('change', () => {
          const indicator = document.getElementById('unsavedIndicator');
          if (indicator) indicator.style.display = 'block';
        });
      }
    });
  },

  _renderEditTab(tab, container) {
    const e = this.data.entrega;
    const fluidos = e.niveles_fluidos || [];

    switch (tab) {
      case 'inspeccion':
        container.innerHTML = `
          ${this._renderAccordionSection('edit_inspeccion_fisica', 'Inspección Física', ITEMS_INSPECCION_FISICA, 'edit-inspeccion', this._editData.inspeccion)}
          ${this._renderAccordionSection('edit_sistema', 'Funcionamiento de Sistema', SISTEMAS_FUNCIONAMIENTO, 'edit-sistema', this._editData.sistema)}
          ${this._renderAccordionSection('edit_cabina', 'Cabina', ITEMS_CABINA, 'edit-cabina', this._editData.cabina)}
          ${this._renderAccordionSection('edit_indicacion', 'Indicaciones del Manual', ITEMS_INDICACIONES_MANUAL, 'edit-indicacion', this._editData.indicacion)}
        `;
        break;

      case 'fluidos':
        container.innerHTML = `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
            <div class="nivel-fluidos-grid">
              ${TIPOS_FLUIDOS.map((fl, i) => {
                const segmento = this._fluidosNiveles?.[`edit_fluido_${fl.key}`] || 0;
                return this._renderNivelBar(fl.key, fl.display, segmento, false, `edit_fluido_${fl.key}`);
              }).join('')}
            </div>
          </div>
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Tapones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.25rem;">
                ${TIPOS_FLUIDOS.map((fl, i) => {
                  const checked = this._editData.fluido_tapon[i] || false;
                  return `
                    <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer; padding: 0.25rem 0.5rem;">
                      <input type="checkbox" class="edit-fluido-tapon" data-idx="${i}" ${checked ? 'checked' : ''} />
                      <span style="font-size: 0.85rem;">${fl.display.replace('Nivel de ', '')}</span>
                    </label>`;
                }).join('')}
              </div>
          </div>
        `;
        break;

      case 'inspeccion-visual':
        this._injectInspeccionVisualStyles();
        container.innerHTML = `
          <div class="iv-card">
            <div class="iv-header-row">
              <button type="button" class="iv-nav-arrow" aria-label="Vista anterior" onclick="window.entregaDetalleView._navEdSlide(-1)">‹</button>
              <div class="iv-view-pill">
                <strong id="edViewTitle">Frontal</strong>
              </div>
              <button type="button" class="iv-nav-arrow" aria-label="Vista siguiente" onclick="window.entregaDetalleView._navEdSlide(1)">›</button>
            </div>
            <div class="iv-toolbar">
              <div class="iv-mode-toggle">
                <button type="button" id="edModeMov" class="iv-mode-btn iv-mode-move iv-mode-active" onclick="window.entregaDetalleView._setMarkMode(window.entregaDetalleView._activeEdSlide, 'move')">🤚 Mover</button>
                <button type="button" id="edModeAdd" class="iv-mode-btn iv-mode-add" onclick="window.entregaDetalleView._setMarkMode(window.entregaDetalleView._activeEdSlide, 'add')">📍 Marcar</button>
                <button type="button" id="edModeDel" class="iv-mode-btn iv-mode-delete" onclick="window.entregaDetalleView._setMarkMode(window.entregaDetalleView._activeEdSlide, 'delete')">🗑 Eliminar</button>
              </div>
            </div>
            <p id="edModeHint" class="iv-mode-hint"></p>
            <div class="iv-image-area" style="position:relative;max-width:1000px;margin:0 auto;">
              <div class="iv-zoom-stack iv-zoom-overlay">
                <button type="button" class="iv-zoom-btn" aria-label="Aumentar zoom" onclick="window.entregaDetalleView._zoomEdActive(1)">+</button>
                <span id="edZoomLabelActive" class="iv-zoom-lbl">100%</span>
                <button type="button" class="iv-zoom-btn" aria-label="Reducir zoom" onclick="window.entregaDetalleView._zoomEdActive(-1)">−</button>
              </div>
              ${[
                { key: 'tractor-frente', alt: 'Parte Frontal', sfx: 'Frente' },
                { key: 'tractor-trasera', alt: 'Parte Trasera', sfx: 'Trasera' },
                { key: 'tractor-lateral-izq', alt: 'Lateral Izquierdo', sfx: 'LatIzq' },
                { key: 'tractor-lateral-der', alt: 'Lateral Derecho', sfx: 'LatDer' }
              ].map((s, i) => `
                <div id="editSlide-${s.key}" style="display:${i === 0 ? 'block' : 'none'};">
                  <div id="scroll${s.sfx}" style="overflow: hidden; border: 1px solid var(--gray-300); border-radius: 8px; background: #fff; text-align: center;">
                    <div id="wrap${s.sfx}" style="position: relative; display: inline-block; cursor: grab;">
                      <img id="img${s.sfx}" src="/img/${s.key}.jpeg" alt="${s.alt}" style="display: block; width: 100%;" draggable="false" />
                      <canvas id="canvas${s.sfx}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; touch-action: auto; user-select: none; -webkit-user-select: none; pointer-events: auto;"></canvas>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
            <p class="iv-hint-keyboard" style="font-size:0.75rem;color:var(--text-muted);text-align:center;margin-top:0.5rem;">⌨️ También puedes usar las flechas ← → del teclado</p>
            <p style="font-size: 0.75rem; color: var(--text-muted); text-align: center; margin-top: 0.25rem;">*El diagrama es ilustrativo, y las características del tractor pueden variar según modelo.</p>
          </div>
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea id="editObservacionesVisual" class="form-input" rows="4" placeholder="Describa hallazgos de la inspección visual...">${this._editData.observaciones_visual || ''}</textarea>
          </div>
        `;
        this._zoomLevels = { 'tractor-frente': 1, 'tractor-trasera': 1, 'tractor-lateral-izq': 1, 'tractor-lateral-der': 1 };
        // Default "move" (Mover): permite pan/scroll/zoom desde el primer toque.
        // El usuario switchea a "Marcar" como acción consciente.
        this._markMode = { 'tractor-frente': 'move', 'tractor-trasera': 'move', 'tractor-lateral-izq': 'move', 'tractor-lateral-der': 'move' };
        this._activeEdSlide = 'tractor-frente';
        this._edSlidesOrder = ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
        setTimeout(() => {
          this._initAnnotationCanvas('canvasFrente', 'imgFrente', 'tractor-frente');
          this._initAnnotationCanvas('canvasTrasera', 'imgTrasera', 'tractor-trasera');
          this._initAnnotationCanvas('canvasLatIzq', 'imgLatIzq', 'tractor-lateral-izq');
          this._initAnnotationCanvas('canvasLatDer', 'imgLatDer', 'tractor-lateral-der');
          this._updateEdHeader();
          this._updateEdModeHint(this._markMode?.[this._activeEdSlide] || 'move');
        }, 100);
        // Atajo de teclado ← → para navegar entre vistas (no roba foco a inputs).
        if (!this._edKeydownHandler) {
          this._edKeydownHandler = (e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            const card = document.querySelector('.iv-card');
            if (!card || card.offsetParent === null) return;
            this._navEdSlide(e.key === 'ArrowRight' ? 1 : -1);
          };
          document.addEventListener('keydown', this._edKeydownHandler);
        }
        break;

      case 'observaciones':
        container.innerHTML = `
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea id="editObservaciones" class="form-input" rows="4" placeholder="Observaciones generales...">${this._editData.observaciones || ''}</textarea>
          </div>
        `;
        break;

      case 'firmas': {
        const renderFirmaBox = (tipo, label, nombre, url) => {
          const cuerpo = url ? `
            <div style="padding: 1.5rem; text-align: center;">
              <img src="${url}" style="width: 100%; max-height: 100px; object-fit: contain;" onerror="this.style.display='none'" />
            </div>
            <div style="padding: 0 1rem 0.25rem; text-align: center;">
              <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.entregaDetalleView.cambiarFirmaExistente('${tipo}')">Cambiar firma</a>
            </div>
          ` : `
            <div class="firma-box" style="position: relative;">
              <canvas id="firma-${tipo}" class="firma-canvas-new"></canvas>
              <span class="firma-placeholder" id="placeholder-${tipo}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; pointer-events: none; color: var(--gray-400);">
                <span style="font-size: 0.8rem;">Firme aquí</span>
              </span>
            </div>
            <div style="padding: 0 1rem 0.25rem; text-align: center;">
              <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.entregaDetalleView.limpiarFirma('${tipo}')">Limpiar</a>
            </div>
          `;
          return `
            <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
              <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
                <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">${label} *</span>
              </div>
              ${cuerpo}
              <div style="padding: 0.5rem 1rem 0.75rem; border-top: 1px solid var(--gray-200);">
                <span style="font-size: 0.7rem; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.5px;">Nombre</span>
                <p style="margin: 0.15rem 0 0; font-weight: 600; font-size: 0.85rem;">${nombre || '-'}</p>
              </div>
            </div>
          `;
        };

        container.innerHTML = `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="ent-grid-2col">
              ${renderFirmaBox('distribuidor', 'Distribuidor', e.distribuidor, e.firma_distribuidor_url)}
              ${renderFirmaBox('usuario', 'Usuario / Cliente', e.usuario, e.firma_usuario_url)}
            </div>
          </div>
        `;
        break;
      }
    }

    // Detectar cambios
    container.querySelectorAll('input, select, textarea').forEach(el => {
      el.addEventListener('change', () => {
        this._collectEditTabData();
        const indicator = document.getElementById('unsavedIndicator');
        if (indicator) indicator.style.display = 'block';
      });
    });
  },

  _collectEditTabData() {
    // Solo recolectar si los elementos están en el DOM (tabs se renderizan uno a la vez)
    const inspeccionCbs = document.querySelectorAll('[name^="edit-inspeccion_"]');
    if (inspeccionCbs.length > 0) {
      inspeccionCbs.forEach(cb => {
        const idx = parseInt(cb.name.replace('edit-inspeccion_', ''));
        this._editData.inspeccion[idx] = cb.checked;
      });
    }
    const sistemaCbs = document.querySelectorAll('[name^="edit-sistema_"]');
    if (sistemaCbs.length > 0) {
      sistemaCbs.forEach(cb => {
        const idx = parseInt(cb.name.replace('edit-sistema_', ''));
        this._editData.sistema[idx] = cb.checked;
      });
    }
    const cabinaCbs = document.querySelectorAll('[name^="edit-cabina_"]');
    if (cabinaCbs.length > 0) {
      cabinaCbs.forEach(cb => {
        const idx = parseInt(cb.name.replace('edit-cabina_', ''));
        this._editData.cabina[idx] = cb.checked;
      });
    }
    const indicacionCbs = document.querySelectorAll('[name^="edit-indicacion_"]');
    if (indicacionCbs.length > 0) {
      indicacionCbs.forEach(cb => {
        const idx = parseInt(cb.name.replace('edit-indicacion_', ''));
        this._editData.indicacion[idx] = cb.checked;
      });
    }
    // Tapones (solo si están en el DOM)
    const taponChecks = document.querySelectorAll('.edit-fluido-tapon');
    if (taponChecks.length > 0) {
      taponChecks.forEach(cb => {
        this._editData.fluido_tapon[cb.dataset.idx] = cb.checked;
      });
    }
    // Observaciones
    const obs = document.getElementById('editObservaciones');
    if (obs) this._editData.observaciones = obs.value;
    const obsV = document.getElementById('editObservacionesVisual');
    if (obsV) this._editData.observaciones_visual = obsV.value;
    const crm = document.getElementById('editReporteCRM');
    if (crm) this._editData.reporte_crm = crm.value;
  },

  async guardarEdicion() {
    const e = this.data.entrega;

    // Recolectar datos del tab actual
    this._collectEditTabData();

    const inspeccion_fisica = ITEMS_INSPECCION_FISICA.map((item, i) => ({
      item,
      verificado: !!(this._editData.inspeccion[i])
    }));

    const funcionamiento_sistema = SISTEMAS_FUNCIONAMIENTO.map((sistema, i) => ({
      sistema,
      verificado: !!(this._editData.sistema[i])
    }));

    const cabina = ITEMS_CABINA.map((item, i) => ({
      item,
      verificado: !!(this._editData.cabina[i])
    }));

    const indicaciones_manual = ITEMS_INDICACIONES_MANUAL.map((item, i) => ({
      item,
      verificado: !!(this._editData.indicacion[i])
    }));

    const niveles_fluidos = TIPOS_FLUIDOS.map((fl, i) => {
      const segKey = `edit_fluido_${fl.key}`;
      const segmento = (this._fluidosNiveles && this._fluidosNiveles[segKey]) || 0;
      return {
        fluido: fl.display,
        nivel: SEGMENTO_A_NIVEL[segmento] || null,
        tapon_verificado: this._editData.fluido_tapon[i] !== undefined ? this._editData.fluido_tapon[i] : true
      };
    });

    // Eliminar fotos de GCS de marcas eliminadas
    if (this._fotosEliminar && this._fotosEliminar.length > 0) {
      for (const url of this._fotosEliminar) {
        await recepcionService.eliminarArchivo(url).catch(err => console.error('Error al eliminar foto de GCS:', err));
      }
      this._fotosEliminar = [];
    }

    // Subir fotos pendientes a GCS — bucket unificado entrega/{num_serie}/fotos/
    if (this._fotosPendientes && this._fotosPendientes.length > 0) {
      this._showProgress('Subiendo fotos...');
      for (const marca of this._fotosPendientes) {
        if (marca._foto_pendiente) {
          try {
            const url = await recepcionService.subirFoto(
              marca._foto_pendiente,
              `ENT-${e.id}_danio_${Date.now()}.jpg`,
              e.num_serie,
              'entrega'
            );
            marca.foto_url = url;
          } catch (err) {
            console.error('Error al subir foto:', err);
          }
          delete marca._foto_pendiente;
          delete marca._foto_preview;
        }
      }
      this._fotosPendientes = [];
    }

    // Limpiar datos internos de marcas antes de enviar
    const marcasLimpias = (this._editData.marcas_visuales || []).map(m => ({
      img: m.img,
      x: m.x,
      y: m.y,
      radius_pct: (typeof m.radius_pct === 'number' && m.radius_pct > 0) ? m.radius_pct : undefined,
      foto_url: m.foto_url || null,
      observacion: m.observacion || null,
      fecha_marca: m.fecha_marca || null
    }));

    try {
      this._showProgress('Preparando datos...');

      // Subir firmas dibujadas al bucket unificado entrega/{num_serie}/firmas/
      // Guarda por URL: solo subir si NO existe URL persistida. "Cambiar firma" pone la URL a null,
      // así que la única vía para re-subir es pasando explícitamente por ese flujo.
      let firmaDistUrl = e.firma_distribuidor_url || null;
      let firmaUsuarioUrl = e.firma_usuario_url || null;

      if (!firmaDistUrl && this.signaturePadDistribuidor && !this.signaturePadDistribuidor.isEmpty()) {
        this._showProgress('Subiendo firma del distribuidor...');
        firmaDistUrl = await recepcionService.subirFirma(
          await this.toBlob(this.signaturePadDistribuidor),
          `ENT-${e.id}_firma_distribuidor.png`,
          e.num_serie,
          'entrega'
        );
      }
      if (!firmaUsuarioUrl && this.signaturePadUsuario && !this.signaturePadUsuario.isEmpty()) {
        this._showProgress('Subiendo firma del usuario...');
        firmaUsuarioUrl = await recepcionService.subirFirma(
          await this.toBlob(this.signaturePadUsuario),
          `ENT-${e.id}_firma_usuario.png`,
          e.num_serie,
          'entrega'
        );
      }

      const data = {
        fecha: document.getElementById('editFecha')?.value || undefined,
        horas_motor: parseFloat(document.getElementById('editHorasMotor')?.value) || undefined,
        observaciones: this._editData.observaciones || null,
        observaciones_visual: this._editData.observaciones_visual || null,
        reporte_crm: document.getElementById('editReporteCRM')?.value || null,
        inspeccion_fisica,
        funcionamiento_sistema,
        cabina,
        indicaciones_manual,
        niveles_fluidos,
        marcas_visuales: marcasLimpias,
        firma_distribuidor_url: firmaDistUrl,
        firma_usuario_url: firmaUsuarioUrl
      };

      this._showProgress('Guardando cambios...');
      await ventasService.actualizarEntrega(e.id, data);
      this._hideProgress();
      toast.success('Entrega actualizada');
      const indicator = document.getElementById('unsavedIndicator');
      if (indicator) indicator.style.display = 'none';

      // Recargar para reset de pads + re-render con URLs actualizadas (patrón OT/Liberación)
      await this.cargarEntrega(e.id);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al actualizar');
      console.error(error);
    }
  },

  cancelarEdicion() {
    this.data.mode = 'view';
    this.renderEntregaR8();
  },

  async descargarPDF() {
    const e = this.data.entrega;
    if (!e) return;

    toast.info('Generando PDF...');

    try {
      // Cargar html2pdf.js desde CDN si no está cargado
      if (typeof html2pdf === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Error al cargar librería PDF'));
          document.head.appendChild(script);
        });
      }

      // Generar HTML usando PrintEntrega, envuelto en un contenedor captureable
      const html = PrintEntrega.generateHTML(e);

      // Crear contenedor temporal
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed; left:-9999px; top:0; width:210mm;';
      container.innerHTML = `<div class="ep-pdf-wrapper">${html}</div>`;
      document.body.appendChild(container);

      // Esperar a que las imágenes carguen
      const imgs = container.querySelectorAll('img');
      await Promise.all(Array.from(imgs).map(img =>
        img.complete ? Promise.resolve() : new Promise(r => { img.onload = r; img.onerror = r; })
      ));

      window.scrollTo(0, 0);
      await new Promise(r => setTimeout(r, 500));

      // Capturar el wrapper interno (no el contenedor posicionado)
      const target = container.querySelector('.ep-pdf-wrapper');

      // Generar y descargar PDF
      await html2pdf().set({
        margin: 0,
        filename: `Entrega_${e.num_serie}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all', before: '.ep-page2' }
      }).from(target).save();

      // Limpiar
      document.body.removeChild(container);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
      console.error(error);
    }
  },

  eliminarEntrega() {
    const e = this.data.entrega;
    modal.confirm(
      'Eliminar Entrega',
      `¿Está seguro de eliminar la entrega del tractor ${e.num_serie}? Esta acción no se puede deshacer.`,
      async () => {
        try {
          await ventasService.eliminarEntrega(e.id);
          toast.success('Entrega eliminada');
          router.navigate('/ventas/entregas');
        } catch (error) {
          toast.error(error.message || 'Error al eliminar entrega');
          console.error(error);
        }
      }
    );
  },

  async completarEntrega() {
    // Si hay cambios pendientes, exigir guardar primero (patrón Recepciones/OT/Checklist/Liberaciones)
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator && indicator.style.display !== 'none') {
      toast.warning('Guarde los cambios antes de completar la entrega');
      return;
    }

    const e = this.data.entrega;
    const errores = [];

    // Recolectar datos actuales
    this._collectEditTabData();

    // 1. Inspección Física
    const inspeccionTotal = ITEMS_INSPECCION_FISICA.length;
    const inspeccionChecked = Object.values(this._editData.inspeccion || {}).filter(v => v).length;
    if (inspeccionChecked < inspeccionTotal) {
      errores.push({ msg: 'Debe completar toda la inspección física', tab: 'inspeccion' });
    }

    // 2. Funcionamiento de Sistema
    const sistemaTotal = SISTEMAS_FUNCIONAMIENTO.length;
    const sistemaChecked = Object.values(this._editData.sistema || {}).filter(v => v).length;
    if (sistemaChecked < sistemaTotal) {
      errores.push({ msg: 'Debe completar todo el funcionamiento de sistema', tab: 'inspeccion' });
    }

    // 3. Cabina
    const cabinaTotal = ITEMS_CABINA.length;
    const cabinaChecked = Object.values(this._editData.cabina || {}).filter(v => v).length;
    if (cabinaChecked < cabinaTotal) {
      errores.push({ msg: 'Debe completar toda la cabina', tab: 'inspeccion' });
    }

    // 4. Indicaciones del Manual
    const indicacionTotal = ITEMS_INDICACIONES_MANUAL.length;
    const indicacionChecked = Object.values(this._editData.indicacion || {}).filter(v => v).length;
    if (indicacionChecked < indicacionTotal) {
      errores.push({ msg: 'Debe completar todas las indicaciones del manual', tab: 'inspeccion' });
    }

    // 5. Niveles de fluidos
    const nivelesCompletos = TIPOS_FLUIDOS.every((fl) => {
      const segKey = `edit_fluido_${fl.key}`;
      return (this._fluidosNiveles && this._fluidosNiveles[segKey]) > 0;
    });
    if (!nivelesCompletos) {
      errores.push({ msg: 'Debe seleccionar el nivel de todos los fluidos', tab: 'fluidos' });
    }

    // 6. Tapones
    const taponesTotal = TIPOS_FLUIDOS.length;
    const taponesChecked = Object.values(this._editData.fluido_tapon || {}).filter(v => v).length;
    if (taponesChecked < taponesTotal) {
      errores.push({ msg: 'Debe verificar todos los tapones', tab: 'fluidos' });
    }

    // 7. Reporte CRM
    const crmVal = document.getElementById('editReporteCRM')?.value || '';
    if (!crmVal.trim()) {
      errores.push({ msg: 'El Reporte CRM es obligatorio', tab: 'inspeccion' });
    }

    // 8. Firmas (persistidas — ya se guardaron con "Guardar cambios")
    if (!e.firma_distribuidor_url) errores.push({ msg: 'Falta la firma del distribuidor', tab: 'firmas' });
    if (!e.firma_usuario_url) errores.push({ msg: 'Falta la firma del usuario', tab: 'firmas' });

    if (errores.length > 0) {
      this.showTab(errores[0].tab);
      toast.error(errores[0].msg);
      return;
    }

    modal.confirm(
      'Completar Entrega',
      '¿Está seguro de completar esta entrega? Una vez completada no podrá ser editada.',
      async () => {
        try {
          this._showProgress('Completando entrega...');
          // Firmas ya persistidas con "Guardar cambios"
          await ventasService.completarEntrega(e.id, {
            firma_distribuidor_url: e.firma_distribuidor_url,
            firma_usuario_url: e.firma_usuario_url,
            reporte_crm: this._editData.reporte_crm || null,
            observaciones_visual: this._editData.observaciones_visual || null
          });

          this._hideProgress();
          toast.success('Entrega completada');
          this.data.mode = 'view';
          // Recargar para reset de pads + re-render
          await this.cargarEntrega(e.id);
        } catch (error) {
          this._hideProgress();
          toast.error(error.message || 'Error al completar entrega');
          console.error(error);
        }
      }
    );
  },

  // =====================================================
  // SIGNATURE PAD
  // =====================================================

  loadSignaturePad() {
    if (typeof SignaturePad !== 'undefined') {
      this.initFirmas();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
    script.onload = () => this.initFirmas();
    document.head.appendChild(script);
  },

  initFirmas() {
    const canvasDist = document.getElementById('firma-distribuidor');
    const canvasUsuario = document.getElementById('firma-usuario');
    // Salir solo si ningún canvas está disponible
    if (!canvasDist && !canvasUsuario) return;

    const resizeCanvas = (canvas) => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const parent = canvas.parentElement;
      const width = parent ? parent.clientWidth : canvas.offsetWidth;
      const height = canvas.offsetHeight || 120;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      canvas.getContext('2d').scale(ratio, ratio);
    };

    const initPad = (canvas) => {
      resizeCanvas(canvas);
      const pad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)'
      });
      const hidePlaceholder = () => {
        const placeholder = canvas.parentElement.querySelector('.firma-placeholder');
        if (placeholder) placeholder.style.display = 'none';
      };
      canvas.addEventListener('pointerdown', hidePlaceholder);
      canvas.addEventListener('touchstart', hidePlaceholder);

      // Activar "Hay cambios sin guardar" al terminar un trazo
      if (this._showUnsaved) pad.addEventListener('endStroke', () => this._showUnsaved());

      return pad;
    };

    this.signaturePadDistribuidor = canvasDist ? initPad(canvasDist) : null;
    this.signaturePadUsuario = canvasUsuario ? initPad(canvasUsuario) : null;
  },

  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700);"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  },

  _showUnsaved() {
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  cambiarFirmaExistente(tipo) {
    const e = this.data.entrega;
    if (!e) return;
    if (tipo === 'distribuidor') e.firma_distribuidor_url = null;
    if (tipo === 'usuario') e.firma_usuario_url = null;
    this[`signaturePad${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`] = null;
    this._showUnsaved();
    // Re-renderizar tab firmas (showTab ya invoca loadSignaturePad)
    this.showTab('firmas');
  },

  limpiarFirma(tipo) {
    if (tipo === 'distribuidor' && this.signaturePadDistribuidor) {
      this.signaturePadDistribuidor.clear();
      const ph = document.getElementById('placeholder-distribuidor');
      if (ph) ph.style.display = '';
    }
    if (tipo === 'usuario' && this.signaturePadUsuario) {
      this.signaturePadUsuario.clear();
      const ph = document.getElementById('placeholder-usuario');
      if (ph) ph.style.display = '';
    }
  },

  async toBlob(signaturePad) {
    const dataURL = signaturePad.toDataURL('image/png');
    const response = await fetch(dataURL);
    return response.blob();
  },

  // =====================================================
  // VISTA LEGACY (entregas anteriores al formato R8)
  // =====================================================

  renderEntregaLegacy() {
    const container = document.getElementById('entregaContent');
    const e = this.data.entrega;

    container.innerHTML = `
      <!-- Encabezado -->
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem;">
          <div><strong>Num. Serie:</strong><br><span style="font-size: 1.1rem;">${e.num_serie}</span></div>
          <div><strong>Fecha:</strong><br>${new Date(e.fecha).toLocaleDateString('es-MX')}</div>
          <div><strong>Usuario:</strong><br>${e.usuario}</div>
          <div><strong>Horas Motor:</strong><br>${e.horas_motor} hrs</div>
          <div><strong>Modelo:</strong><br>${e.modelo || '-'}</div>
          <div>
            <strong>Estado:</strong><br>
            <span class="badge ${e.completada ? 'badge-success' : 'badge-warning'}">
              ${e.completada ? 'Completada' : 'Pendiente'}
            </span>
          </div>
        </div>
        <div style="margin-top: 0.5rem;">
          <span class="badge badge-info">Formato anterior</span>
        </div>
      </div>

      <!-- Inspección Física -->
      ${e.inspeccion_fisica && e.inspeccion_fisica.length > 0 ? `
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
          <h3 style="margin-bottom: 1rem;">Inspección Física</h3>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.25rem;">
            ${e.inspeccion_fisica.map(item => this._renderCheckItem(item.item, item.verificado)).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Sistemas -->
      ${e.sistemas && e.sistemas.length > 0 ? `
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
          <h3 style="margin-bottom: 1rem;">Sistemas de Funcionamiento</h3>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.25rem;">
            ${e.sistemas.map(s => this._renderCheckItem(s.sistema, s.verificado)).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Fluidos -->
      ${e.niveles_fluidos && e.niveles_fluidos.length > 0 ? `
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
          <h3 style="margin-bottom: 1rem;">Niveles de Fluidos</h3>
          <div class="table-container">
            <table class="table">
              <thead><tr><th>Fluido</th><th>Nivel</th><th>Tapón OK</th></tr></thead>
              <tbody>
                ${e.niveles_fluidos.map(f => `
                  <tr>
                    <td>${f.fluido}</td>
                    <td><span class="badge">${f.nivel_visual || '-'}</span></td>
                    <td>${f.tapon_verificado ? '<span style="color: var(--success);">✓</span>' : '<span style="color: var(--danger);">✗</span>'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}

      ${e.observaciones ? `
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
          <h3 style="margin-bottom: 0.5rem;">Observaciones</h3>
          <p>${e.observaciones}</p>
        </div>
      ` : ''}

      <div style="display: flex; gap: 1rem; justify-content: flex-end;">
        <button class="btn btn-secondary" onclick="router.navigate('/ventas/entregas')">
          Volver
        </button>
      </div>
    `;
  },

  // =====================================================
  // UTILIDADES
  // =====================================================

  _renderSelectAll(section) {
    return `
      <label style="display: inline-flex; align-items: center; gap: 0.5rem; cursor: pointer; font-size: 0.75rem; color: var(--text-muted); user-select: none;">
        <span style="font-size: 0.75rem;">Seleccionar todo</span>
        <span class="toggle-switch">
          <input type="checkbox" class="toggle-input" data-selectall="${section}" onchange="window.entregaDetalleView.toggleAll('${section}', this.checked)" />
          <span class="toggle-slider"></span>
        </span>
      </label>
    `;
  },

  _renderSectionHeader(title, section) {
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
        <h3 style="margin: 0; color: var(--primary);">${title}</h3>
      </div>
    `;
  },


  toggleAll(section, checked) {
    // En modo new: name="inspeccion_0", name="sistema_0", etc.
    // En modo edit: class="edit-inspeccion", class="edit-sistema", etc.
    const items = document.querySelectorAll(`[name^="${section}_"], .${section}`);

    if (this.data.mode === 'edit') {
      if (!this._toggleSnapshot) this._toggleSnapshot = {};

      if (checked) {
        // Guardar estado previo antes de marcar todo
        this._toggleSnapshot[section] = {};
        items.forEach((cb, i) => {
          this._toggleSnapshot[section][i] = cb.checked;
          cb.checked = true;
        });
      } else {
        // Restaurar estado previo
        const snapshot = this._toggleSnapshot[section];
        items.forEach((cb, i) => {
          cb.checked = snapshot ? (snapshot[i] || false) : false;
        });
        delete this._toggleSnapshot[section];
      }
    } else {
      items.forEach(cb => { cb.checked = checked; });
    }

    if (this.data.mode === 'new') this._collectCurrentTabData();
    if (this.data.mode === 'edit') this._collectEditTabData();
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  // =====================================================
  // ANOTACIONES EN IMÁGENES (marcas de daño)
  // =====================================================

  _initAnnotationCanvas(canvasId, imgId, imgKey) {
    const canvas = document.getElementById(canvasId);
    const img = document.getElementById(imgId);
    if (!canvas || !img) return;

    const setup = () => {
      canvas.width = img.naturalWidth || img.offsetWidth;
      canvas.height = img.naturalHeight || img.offsetHeight;
      this._drawMarks(canvasId, imgKey);
    };

    if (img.complete) setup();
    else img.onload = setup;

    // Canvas oculto para detectar pixeles de la imagen (fondo blanco vs tractor)
    const _hitCanvas = document.createElement('canvas');
    const _hitCtx = _hitCanvas.getContext('2d');
    const _loadHitCanvas = () => {
      _hitCanvas.width = img.naturalWidth;
      _hitCanvas.height = img.naturalHeight;
      _hitCtx.drawImage(img, 0, 0);
    };
    if (img.complete) _loadHitCanvas();
    else img.addEventListener('load', _loadHitCanvas);

    if (!this._fotosPendientes) this._fotosPendientes = [];

    // Drag state para "arrastrar para dimensionar". Si el usuario solo hace
    // pointerdown+pointerup sin moverse, cae al tamaño default (2.5%).
    let dragState = null;
    let clickedExisting = null;
    // El contenedor con overflow:auto cambia según la vista. Lo resolvemos
    // a partir del sufijo del canvasId (canvasFrente → scrollFrente). El
    // drag-scroll con mouse lo maneja `_attachScrollPan` en el contenedor.
    const sfx = canvasId.replace('canvas', '');
    const scrollContainer = document.getElementById(`scroll${sfx}`);
    this._attachScrollPan(scrollContainer, () => (this._zoomLevels && this._zoomLevels[imgKey]) || 1, () => (this._markMode?.[imgKey] || 'move') === 'move');

    const getCoords = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      return { x, y };
    };

    const findMarkAt = (x, y) => {
      // Hit radius dinámico: depende del radio de cada marca para que las
      // pequeñas también sean clickeables. Mínimo 2% del canvas.
      return (this._editData.marcas_visuales || []).find(m => {
        if (m.img !== imgKey) return false;
        const r = Math.max(2, (typeof m.radius_pct === 'number' && m.radius_pct > 0) ? m.radius_pct : 2.5);
        return Math.abs(m.x - x) < r && Math.abs(m.y - y) < r;
      });
    };

    const isPixelOnTractor = (x, y) => {
      const px = Math.round((x / 100) * _hitCanvas.width);
      const py = Math.round((y / 100) * _hitCanvas.height);
      try {
        const pixel = _hitCtx.getImageData(px, py, 1, 1).data;
        return !(pixel[0] > 240 && pixel[1] > 240 && pixel[2] > 240);
      } catch (err) { return true; }
    };

    canvas.addEventListener('pointerdown', (e) => {
      if (!this._editData.marcas_visuales) this._editData.marcas_visuales = [];
      const { x, y } = getCoords(e);
      const mode = this._markMode?.[imgKey] || 'move';
      clickedExisting = null;
      dragState = null;

      if (mode === 'delete') {
        const marcaExistente = findMarkAt(x, y);
        if (!marcaExistente) return;
        const idx = this._editData.marcas_visuales.indexOf(marcaExistente);
        const numPunto = this._editData.marcas_visuales.filter(m => m.img === imgKey).indexOf(marcaExistente) + 1;
        modal.confirm(
          'Eliminar marca',
          `¿Está seguro de eliminar el punto #${numPunto}?`,
          () => {
            if (marcaExistente.foto_url) {
              if (!this._fotosEliminar) this._fotosEliminar = [];
              this._fotosEliminar.push(marcaExistente.foto_url);
            }
            if (this._fotosPendientes) {
              this._fotosPendientes = this._fotosPendientes.filter(m => m !== marcaExistente);
            }
            this._editData.marcas_visuales.splice(idx, 1);
            this._drawMarks(canvasId, imgKey);
            this._updateEdHeader();
            const indicator = document.getElementById('unsavedIndicator');
            if (indicator) indicator.style.display = 'block';
          }
        );
        return;
      }

      // Tap sobre marca existente. Comportamiento depende del modo:
      //  - move: abrir el visor/captura de foto (consulta).
      //  - add: NO hacer nada (modo enfocado en crear marcas nuevas).
      const marcaExistente = findMarkAt(x, y);
      if (marcaExistente) {
        if (mode === 'move') {
          clickedExisting = marcaExistente;
          return;
        }
        if (mode === 'add') {
          toast.info('Cambia a 🤚 Mover para ver fotos de un punto existente');
          return;
        }
      }


      if (mode !== 'add') return;

      if (!isPixelOnTractor(x, y)) {
        toast.warning('Solo puede marcar sobre la imagen del tractor');
        return;
      }

      // Inicia drag: el centro queda en el pointerdown, el radio se mide en pointerup.
      dragState = { startX: x, startY: y, currentRadiusPct: 0 };
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    });

    canvas.addEventListener('pointermove', (e) => {
      if (!dragState) return;
      const { x, y } = getCoords(e);
      const dx = x - dragState.startX;
      const dy = y - dragState.startY;
      const radiusPct = Math.min(25, Math.hypot(dx, dy));
      dragState.currentRadiusPct = radiusPct;
      this._drawMarks(canvasId, imgKey, {
        img: imgKey,
        x: dragState.startX,
        y: dragState.startY,
        radius_pct: Math.max(radiusPct, 0.5)
      });
    });

    canvas.addEventListener('pointerup', (e) => {
      try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}

      // Si el pointerdown cayó sobre marca existente, este pointerup = click:
      // abrir visor (con foto) o solicitar foto (sin foto).
      if (clickedExisting) {
        const m = clickedExisting;
        clickedExisting = null;
        if (m.foto_url || m._foto_preview) {
          this._verFotoDanio(m, canvasId, imgKey);
        } else {
          this._solicitarFotoDanio(m, canvasId, imgKey);
        }
        return;
      }

      if (!dragState) return;
      const { startX, startY, currentRadiusPct } = dragState;
      dragState = null;

      // Si el drag fue muy chico (<1%), usa el tamaño default — preserva el
      // "solo click crea marca" para usuarios que no descubren el drag-to-size.
      const DEFAULT_RADIUS_PCT = 2.5;
      const MIN_USER_RADIUS_PCT = 1.0;
      const radius_pct = currentRadiusPct < MIN_USER_RADIUS_PCT
        ? DEFAULT_RADIUS_PCT
        : Math.round(currentRadiusPct * 10) / 10;

      const now = new Date();
      const fechaMarca = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
      const marca = {
        img: imgKey,
        x: Math.round(startX * 10) / 10,
        y: Math.round(startY * 10) / 10,
        radius_pct,
        foto_url: null,
        fecha_marca: fechaMarca
      };
      this._editData.marcas_visuales.push(marca);
      this._drawMarks(canvasId, imgKey);
      this._updateEdHeader();
      const indicator = document.getElementById('unsavedIndicator');
      if (indicator) indicator.style.display = 'block';
      this._solicitarFotoDanio(marca, canvasId, imgKey);
    });

    canvas.addEventListener('pointercancel', () => {
      dragState = null;
      clickedExisting = null;
      this._drawMarks(canvasId, imgKey);
    });
  },

  _solicitarFotoDanio(marca, canvasId, imgKey) {
    if (!this._fotosPendientes) this._fotosPendientes = [];

    const overlay = document.createElement('div');
    overlay.id = 'modalFotoDanio';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    const ubicacionMap = {
      'tractor-frente': 'Frontal',
      'tractor-trasera': 'Trasera',
      'tractor-lateral-izq': 'Lateral Izquierdo',
      'tractor-lateral-der': 'Lateral Derecho'
    };
    const numPunto = (this._editData.marcas_visuales || []).filter(m => m.img === imgKey).length;
    const ubicacion = ubicacionMap[imgKey] || imgKey;

    overlay.innerHTML = `
      <div style="background:#fff;border-radius:16px;width:92%;max-width:800px;box-shadow:0 16px 48px rgba(0,0,0,0.4);overflow:hidden;max-height:90vh;overflow-y:auto;">
        <div style="background:var(--primary);padding:1.25rem 1.25rem 0.75rem;display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <h3 style="margin:0 0 0.15rem;font-size:1rem;font-weight:700;color:#fff;">Punto #${numPunto} — ${ubicacion}</h3>
            <p style="margin:0;font-size:0.8rem;color:rgba(255,255,255,0.7);" id="fotoDanioSubtitulo">Capture la evidencia del daño</p>
          </div>
          <button type="button" id="fotoDanioCerrar" style="background:none;border:none;color:rgba(255,255,255,0.6);font-size:1.3rem;cursor:pointer;padding:0;line-height:1;">&times;</button>
        </div>
        <div style="padding:1rem 1.25rem;">
          <div id="fotoDanioPreview" style="display:none;margin-bottom:0.75rem;background:#f8f9fa;border-radius:10px;padding:0.5rem;text-align:center;">
            <img id="fotoDanioImg" style="width:100%;max-height:220px;object-fit:contain;border-radius:8px;" />
          </div>
          <div id="fotoDanioCaptura" style="margin-bottom:0.75rem;">
            <div style="display:flex;gap:0.5rem;">
              <button type="button" id="fotoDanioBtnCamera" style="flex:1;display:flex;flex-direction:column;align-items:center;gap:0.35rem;padding:1.5rem 0.5rem;border:2px dashed var(--primary-light);border-radius:10px;cursor:pointer;background:rgba(37,99,235,0.04);">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                <span style="font-size:0.75rem;font-weight:600;color:var(--primary);">Tomar foto</span>
              </button>
              <label style="flex:1;display:flex;flex-direction:column;align-items:center;gap:0.35rem;padding:1.5rem 0.5rem;border:2px dashed #dee2e6;border-radius:10px;cursor:pointer;background:#f8f9fa;">
                <input type="file" accept="image/*" id="fotoDanioInputGaleria" style="display:none;" />
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6c757d" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <span style="font-size:0.75rem;font-weight:600;color:#6c757d;">Galería</span>
              </label>
            </div>
          </div>
          <!-- Cámara in-page: video stream + controles. Evita la app nativa
               (eviction de la pestaña en mobile al volver = reload + marcas perdidas). -->
          <div id="fotoDanioCameraView" style="display:none;margin-bottom:0.75rem;background:#000;border-radius:10px;overflow:hidden;">
            <video id="fotoDanioVideo" autoplay playsinline muted style="width:100%;max-height:60vh;display:block;background:#000;object-fit:cover;"></video>
            <div style="display:flex;gap:0.5rem;padding:0.6rem;background:#111;">
              <button type="button" id="fotoDanioCameraCancelar" style="flex:1;padding:0.6rem;border:none;border-radius:8px;background:#444;color:#fff;font-size:0.85rem;font-weight:600;cursor:pointer;">Cancelar</button>
              <button type="button" id="fotoDanioCameraSwitch" title="Cambiar cámara" style="width:48px;padding:0.6rem;border:none;border-radius:8px;background:#444;color:#fff;font-size:1rem;cursor:pointer;">⇄</button>
              <button type="button" id="fotoDanioCameraCapturar" style="flex:2;padding:0.6rem;border:none;border-radius:8px;background:var(--primary);color:#fff;font-size:0.9rem;font-weight:700;cursor:pointer;">Capturar</button>
            </div>
          </div>
        </div>
        <div style="padding:0 1.25rem;">
          <div id="fotoDanioObsToggle" style="cursor:pointer;padding:0.5rem 0;display:flex;align-items:center;gap:0.35rem;color:var(--primary);font-size:0.8rem;">
            <span id="fotoDanioObsIcon">+</span> Agregar observación (opcional)
          </div>
          <div id="fotoDanioObsWrap" style="display:none;margin-bottom:0.5rem;">
            <textarea id="fotoDanioObs" rows="3" placeholder="Describa el daño observado..." style="width:100%;border:1px solid #dee2e6;border-radius:8px;background:#f8f9fa;color:#333;padding:0.6rem;font-size:0.85rem;resize:none;box-sizing:border-box;"></textarea>
          </div>
        </div>
        <div style="padding:0.75rem 1.25rem 1.25rem;display:flex;gap:0.75rem;">
          <button type="button" id="fotoDanioCancelar" style="flex:1;padding:0.7rem;border:1px solid #dee2e6;border-radius:10px;background:#fff;color:#333;font-size:0.9rem;font-weight:600;cursor:pointer;">Cancelar</button>
          <button type="button" id="fotoDanioGuardar" style="flex:1;padding:0.7rem;border:none;border-radius:10px;background:#dee2e6;color:#adb5bd;font-size:0.9rem;font-weight:600;cursor:not-allowed;display:flex;align-items:center;justify-content:center;gap:0.35rem;" disabled>Guardar <span style="font-size:1rem;">&#10003;</span></button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    let archivoSeleccionado = null;
    let previewDataUrl = null;
    let activeStream = null;
    let cameraFacingMode = 'environment';

    const handleFile = async (file) => {
      // Comprimir antes de guardar para que la galería no mande archivos
      // crudos que rebasen el límite de 5 MB del backend (LIMIT_FILE_SIZE).
      // La cámara in-page ya entrega archivos chicos vía constraints+JPEG 0.85,
      // así que comprimir aquí es redundante pero inofensivo (~50 ms extra).
      const compressed = await compressImage(file);
      archivoSeleccionado = compressed;
      const reader = new FileReader();
      reader.onload = (ev) => {
        previewDataUrl = ev.target.result;
        document.getElementById('fotoDanioImg').src = previewDataUrl;
        document.getElementById('fotoDanioPreview').style.display = 'block';
        document.getElementById('fotoDanioCaptura').style.display = 'none';
        document.getElementById('fotoDanioCameraView').style.display = 'none';
        document.getElementById('fotoDanioSubtitulo').textContent = '¿Confirmar esta foto?';
        const btnGuardar = document.getElementById('fotoDanioGuardar');
        btnGuardar.disabled = false;
        btnGuardar.style.background = 'var(--primary)';
        btnGuardar.style.color = '#fff';
        btnGuardar.style.cursor = 'pointer';
      };
      reader.readAsDataURL(compressed);
    };

    const stopCameraStream = () => {
      if (activeStream) {
        activeStream.getTracks().forEach(t => t.stop());
        activeStream = null;
      }
      const video = document.getElementById('fotoDanioVideo');
      if (video) video.srcObject = null;
    };

    const startCamera = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Tu navegador no soporta cámara in-page. Usa "Galería".');
        return;
      }
      try {
        const constraints = {
          video: { facingMode: cameraFacingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        activeStream = stream;
        document.getElementById('fotoDanioCaptura').style.display = 'none';
        document.getElementById('fotoDanioCameraView').style.display = 'block';
        const video = document.getElementById('fotoDanioVideo');
        video.srcObject = stream;
      } catch (err) {
        console.error('Error al abrir cámara:', err);
        const msg = err.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado. Revisa los permisos del navegador o usa "Galería".'
          : 'No se pudo abrir la cámara. Usa "Galería".';
        toast.error(msg);
      }
    };

    const cerrarCamara = () => {
      stopCameraStream();
      document.getElementById('fotoDanioCameraView').style.display = 'none';
      document.getElementById('fotoDanioCaptura').style.display = '';
    };

    const cambiarCamara = async () => {
      cameraFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
      stopCameraStream();
      await startCamera();
    };

    const capturarFoto = () => {
      const video = document.getElementById('fotoDanioVideo');
      if (!video || !video.videoWidth) {
        toast.warning('La cámara todavía no está lista, espera un momento.');
        return;
      }
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      c.getContext('2d').drawImage(video, 0, 0);
      c.toBlob((blob) => {
        if (!blob) { toast.error('No se pudo capturar la imagen.'); return; }
        const file = new File([blob], `foto-danio-${Date.now()}.jpg`, { type: 'image/jpeg' });
        stopCameraStream();
        handleFile(file);
      }, 'image/jpeg', 0.85);
    };

    document.getElementById('fotoDanioBtnCamera').addEventListener('click', startCamera);
    document.getElementById('fotoDanioCameraCancelar').addEventListener('click', cerrarCamara);
    document.getElementById('fotoDanioCameraSwitch').addEventListener('click', cambiarCamara);
    document.getElementById('fotoDanioCameraCapturar').addEventListener('click', capturarFoto);
    document.getElementById('fotoDanioInputGaleria').addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) handleFile(file);
    });

    document.getElementById('fotoDanioObsToggle').addEventListener('click', () => {
      const wrap = document.getElementById('fotoDanioObsWrap');
      const icon = document.getElementById('fotoDanioObsIcon');
      if (wrap.style.display === 'none') {
        wrap.style.display = 'block';
        icon.textContent = '—';
      } else {
        wrap.style.display = 'none';
        icon.textContent = '+';
      }
    });

    const cancelar = () => {
      stopCameraStream();
      const idx = this._editData.marcas_visuales.indexOf(marca);
      if (idx >= 0 && !marca.foto_url && !marca._foto_pendiente) {
        this._editData.marcas_visuales.splice(idx, 1);
        this._drawMarks(canvasId, imgKey);
        if (typeof this._updateEdHeader === 'function') this._updateEdHeader();
      }
      overlay.remove();
    };

    document.getElementById('fotoDanioCancelar').addEventListener('click', cancelar);
    document.getElementById('fotoDanioCerrar').addEventListener('click', cancelar);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) cancelar(); });

    document.getElementById('fotoDanioGuardar').addEventListener('click', () => {
      if (!archivoSeleccionado) return;
      stopCameraStream();
      marca._foto_pendiente = archivoSeleccionado;
      marca._foto_preview = previewDataUrl;
      marca.observacion = (document.getElementById('fotoDanioObs')?.value || '').trim() || null;
      this._fotosPendientes.push(marca);
      this._drawMarks(canvasId, imgKey);
      const indicator = document.getElementById('unsavedIndicator');
      if (indicator) indicator.style.display = 'block';
      toast.success('Foto lista (se subirá al guardar cambios)');
      overlay.remove();
    });
  },

  _verFotoDanio(marca, canvasId, imgKey) {
    const ubicacionMap = {
      'tractor-frente': 'Frontal',
      'tractor-trasera': 'Trasera',
      'tractor-lateral-izq': 'Lateral Izquierdo',
      'tractor-lateral-der': 'Lateral Derecho'
    };
    const img = marca.img || imgKey;
    const ubicacion = ubicacionMap[img] || img;
    const fotoSrc = marca.foto_url || marca._foto_preview || '';
    const marcasDeVista = (this._editData?.marcas_visuales || []).filter(m => m.img === img);
    const numPunto = marcasDeVista.indexOf(marca) + 1;

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:16px;width:95%;max-width:800px;box-shadow:0 16px 48px rgba(0,0,0,0.35);overflow:hidden;max-height:90vh;overflow-y:auto;">
        <div style="background:var(--primary);padding:1rem 1.25rem;display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <h3 style="margin:0 0 0.15rem;font-size:1rem;font-weight:700;color:#fff;">Punto #${numPunto} — ${ubicacion}</h3>
          </div>
          <button type="button" id="verDanioCerrar" style="background:none;border:none;color:rgba(255,255,255,0.6);font-size:1.4rem;cursor:pointer;padding:0;line-height:1;">&times;</button>
        </div>
        <div style="background:#f1f3f5;padding:1rem;text-align:center;">
          ${fotoSrc
            ? `<img src="${fotoSrc}" style="width:100%;max-height:60vh;object-fit:contain;border-radius:8px;" />`
            : `<div style="padding:2rem;color:#adb5bd;"><p style="margin:0;font-size:0.8rem;">Sin foto</p></div>`
          }
        </div>
        <div style="padding:1rem 1.25rem;">
          <div style="margin-bottom:0.5rem;">
            <div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.5px;color:#6c757d;margin-bottom:0.15rem;">Observaciones</div>
            ${marca.observacion
              ? `<p style="margin:0;font-size:0.85rem;color:#333;background:#f8f9fa;padding:0.5rem 0.75rem;border-radius:6px;border-left:3px solid var(--primary);">${marca.observacion}</p>`
              : `<p style="margin:0;font-size:0.8rem;color:#adb5bd;font-style:italic;">Sin observaciones registradas.</p>`
            }
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    document.getElementById('verDanioCerrar').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  },

  _addViewClickHandler(canvasId, imgKey, marcas) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    canvas.addEventListener('click', (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const filtered = marcas.filter(m => m.img === imgKey);
      // Hit radius dinámico: respeta el radius_pct de cada marca.
      const marca = filtered.find(m => {
        const r = Math.max(2, (typeof m.radius_pct === 'number' && m.radius_pct > 0) ? m.radius_pct : 2.5);
        return Math.abs(m.x - x) < r && Math.abs(m.y - y) < r;
      });
      if (marca && (marca.foto_url || marca.observacion)) {
        this._verFotoDanioView(marca, imgKey, filtered);
      }
    });
  },

  _verFotoDanioView(marca, imgKey, marcasDeVista) {
    const ubicacionMap = {
      'tractor-frente': 'Frontal',
      'tractor-trasera': 'Trasera',
      'tractor-lateral-izq': 'Lateral Izquierdo',
      'tractor-lateral-der': 'Lateral Derecho'
    };
    const ubicacion = ubicacionMap[imgKey] || imgKey;
    const fotoSrc = marca.foto_url || '';
    const numPunto = marcasDeVista.indexOf(marca) + 1;

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:16px;width:95%;max-width:800px;box-shadow:0 16px 48px rgba(0,0,0,0.35);overflow:hidden;max-height:90vh;overflow-y:auto;">
        <div style="background:var(--primary);padding:1rem 1.25rem;display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <h3 style="margin:0 0 0.15rem;font-size:1rem;font-weight:700;color:#fff;">Punto #${numPunto} — ${ubicacion}</h3>
          </div>
          <button type="button" id="verDanioCerrarView" style="background:none;border:none;color:rgba(255,255,255,0.6);font-size:1.4rem;cursor:pointer;padding:0;line-height:1;">&times;</button>
        </div>
        <div style="background:#f1f3f5;padding:1rem;text-align:center;">
          ${fotoSrc
            ? `<img src="${fotoSrc}" style="width:100%;max-height:60vh;object-fit:contain;border-radius:8px;" />`
            : `<div style="padding:2rem;color:#adb5bd;"><p style="margin:0;font-size:0.8rem;">Sin foto</p></div>`
          }
        </div>
        <div style="padding:1rem 1.25rem;">
          <div style="margin-bottom:0.5rem;">
            <div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.5px;color:#6c757d;margin-bottom:0.15rem;">Observaciones</div>
            ${marca.observacion
              ? `<p style="margin:0;font-size:0.85rem;color:#333;background:#f8f9fa;padding:0.5rem 0.75rem;border-radius:6px;border-left:3px solid var(--primary);">${marca.observacion}</p>`
              : `<p style="margin:0;font-size:0.8rem;color:#adb5bd;font-style:italic;">Sin observaciones registradas.</p>`
            }
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.getElementById('verDanioCerrarView').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  },

  _drawMarks(canvasId, imgKey, previewMark = null) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const marks = (this._editData?.marcas_visuales || []).filter(m => m.img === imgKey);
    marks.forEach((mark, i) => {
      const px = (mark.x / 100) * canvas.width;
      const py = (mark.y / 100) * canvas.height;
      // radius_pct (% del ancho) → píxeles. Marcas viejas sin el campo: 2.5%.
      const radiusPct = (typeof mark.radius_pct === 'number' && mark.radius_pct > 0) ? mark.radius_pct : 2.5;
      const radius = Math.max(6, (radiusPct / 100) * canvas.width);

      ctx.beginPath();
      ctx.arc(px, py, radius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(220, 53, 69, 0.65)';
      ctx.fill();
      ctx.strokeStyle = '#dc3545';
      ctx.lineWidth = 2.5;
      ctx.stroke();

      if (radius >= 10) {
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(10, radius * 0.9)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(i + 1), px, py + 1);
      }
    });

    // Preview en vivo durante el drag-to-size.
    if (previewMark && previewMark.img === imgKey) {
      const px = (previewMark.x / 100) * canvas.width;
      const py = (previewMark.y / 100) * canvas.height;
      const radius = Math.max(4, (previewMark.radius_pct / 100) * canvas.width);
      ctx.beginPath();
      ctx.arc(px, py, radius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(220, 53, 69, 0.4)';
      ctx.fill();
      ctx.strokeStyle = '#dc3545';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  },

  _undoMark(imgKey) {
    if (!this._editData?.marcas_visuales) return;
    for (let i = this._editData.marcas_visuales.length - 1; i >= 0; i--) {
      if (this._editData.marcas_visuales[i].img === imgKey) {
        this._editData.marcas_visuales.splice(i, 1);
        break;
      }
    }
    const canvasId = imgKey === 'tractor' ? 'canvasTractor' : 'canvasCabinado';
    this._drawMarks(canvasId, imgKey);

    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  _switchViewSlide(imgKey) {
    const slides = ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
    slides.forEach(key => {
      const slide = document.getElementById(`viewSlide-${key}`);
      if (slide) slide.style.display = key === imgKey ? 'block' : 'none';
    });
    this._activeViewSlide = imgKey;
    this._updateViewHeader();
  },

  // Drag-scroll con mouse para pannear la imagen cuando hay zoom > 1.
  // En touch el browser ya hace pan nativo (touch-action:auto en el canvas),
  // pero con mouse hace falta implementarlo. Funciona tanto en read-only
  // como en edición (solo en modo "move").
  // Idempotente: si ya se registró el listener, no duplica.
  _attachScrollPan(scrollEl, getZoomLevel, shouldPan = () => true) {
    if (!scrollEl) return;
    if (scrollEl.dataset.panAttached === '1') return;
    scrollEl.dataset.panAttached = '1';
    scrollEl.style.cursor = 'grab';

    scrollEl.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (!shouldPan()) return;
      if (getZoomLevel() <= 1) return;

      const startX = e.clientX;
      const startY = e.clientY;
      const startScrollLeft = scrollEl.scrollLeft;
      const startScrollTop = scrollEl.scrollTop;
      let moved = false;
      scrollEl.style.cursor = 'grabbing';
      e.preventDefault();

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (!moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) moved = true;
        scrollEl.scrollLeft = startScrollLeft - dx;
        scrollEl.scrollTop = startScrollTop - dy;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        scrollEl.style.cursor = 'grab';
        // Si hubo drag, suprimir el click subsiguiente (que abriría
        // foto/marca por error). Captura solo el primer click siguiente.
        if (moved) {
          const suppress = (ev) => { ev.stopPropagation(); };
          scrollEl.addEventListener('click', suppress, { capture: true, once: true });
          setTimeout(() => {
            try { scrollEl.removeEventListener('click', suppress, { capture: true }); } catch (_) {}
          }, 100);
        }
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  },

  // Inyecta los estilos de Inspección Visual una sola vez. Idempotente:
  // si otro módulo (ej. OT) ya los inyectó con el mismo id, no duplica.
  _injectInspeccionVisualStyles() {
    if (document.getElementById('iv-styles')) return;
    const style = document.createElement('style');
    style.id = 'iv-styles';
    style.textContent = `
      .iv-card { background: var(--bg-secondary); padding: 1rem; border-radius: 8px; }
      .iv-view-pill { display: inline-flex; flex-direction: column; align-items: center; gap: 0.05rem; background: #fff; border: 2px solid var(--primary); border-radius: 14px; padding: 0.5rem 2rem; min-width: 180px; box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12); line-height: 1.2; }
      .iv-view-pill strong { font-size: 1.1rem; color: var(--primary); font-weight: 700; letter-spacing: 0.01em; }
      .iv-view-pill span { font-size: 0.75rem; color: var(--text-muted); }
      .iv-header-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; max-width: 480px; margin: 0 auto 0.75rem; }
      .iv-toolbar { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.75rem; }
      .iv-mode-toggle { display: inline-flex; border: 1px solid var(--gray-300); border-radius: 8px; overflow: hidden; background: #fff; }
      .iv-mode-btn { padding: 0.4rem 0.75rem; border: none; background: #fff; color: var(--text-muted); font-size: 0.8rem; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem; white-space: nowrap; transition: background 0.15s; }
      .iv-mode-btn:not(.iv-mode-active):hover { background: var(--bg-secondary); }
      .iv-mode-active { background: var(--primary); color: #fff; font-weight: 600; }
      .iv-mode-active.iv-mode-delete { background: var(--danger); }
      .iv-mode-active.iv-mode-move { background: var(--info, #0ea5e9); }
      .iv-nav-arrow { flex: 0 0 auto; width: 44px; height: 44px; border-radius: 10px; border: 1px solid var(--gray-300); background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; line-height: 1; box-shadow: 0 1px 3px rgba(0,0,0,0.08); color: var(--text-primary, #1f2937); transition: background 0.15s; }
      .iv-nav-arrow:hover { background: var(--bg-secondary); }
      .iv-nav-arrow:active { background: var(--gray-200); }
      .iv-zoom-stack { display: inline-flex; flex-direction: column; align-items: stretch; gap: 0.1rem; background: #fff; border: 1px solid var(--gray-300); border-radius: 8px; padding: 0.15rem; }
      .iv-zoom-overlay { position: absolute; top: 0.5rem; right: 0.5rem; z-index: 5; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
      .iv-zoom-btn { width: 32px; height: 30px; border: none; background: transparent; cursor: pointer; font-size: 1.05rem; line-height: 1; border-radius: 4px; color: var(--text-primary, #1f2937); display: inline-flex; align-items: center; justify-content: center; }
      .iv-zoom-btn:hover { background: var(--bg-secondary); }
      .iv-zoom-lbl { font-size: 0.65rem; color: var(--text-muted); padding: 0.15rem 0; text-align: center; border-top: 1px solid var(--gray-200); border-bottom: 1px solid var(--gray-200); }
      .iv-mode-hint { font-size: 0.78rem; color: var(--text-muted); text-align: center; margin: 0 0 0.75rem; padding: 0.5rem 0.75rem; background: rgba(37, 99, 235, 0.05); border-left: 3px solid var(--primary); border-radius: 4px; line-height: 1.4; }
      .iv-mode-hint strong { color: var(--primary); font-weight: 600; }
      .iv-mode-hint.iv-mode-hint-delete { background: rgba(220, 38, 38, 0.05); border-left-color: var(--danger); }
      .iv-mode-hint.iv-mode-hint-delete strong { color: var(--danger); }
      .iv-mode-hint.iv-mode-hint-add { background: rgba(37, 99, 235, 0.05); border-left-color: var(--primary); }
      .iv-mode-hint.iv-mode-hint-add strong { color: var(--primary); }
      .iv-mode-hint.iv-mode-hint-move { background: rgba(14, 165, 233, 0.06); border-left-color: var(--info, #0ea5e9); }
      .iv-mode-hint.iv-mode-hint-move strong { color: var(--info, #0ea5e9); }
      @media (max-width: 600px) {
        .iv-card { padding: 0.75rem 0.5rem; }
        .iv-toolbar { flex-direction: column; align-items: stretch; gap: 0.5rem; }
        .iv-mode-toggle { width: 100%; }
        .iv-mode-btn { flex: 1; justify-content: center; padding: 0.55rem 0.3rem; font-size: 0.78rem; }
        .iv-header-row { max-width: 100%; gap: 0.5rem; }
        .iv-view-pill { padding: 0.5rem 1rem; min-width: 0; flex: 1; }
        .iv-view-pill strong { font-size: 1.05rem; }
        .iv-nav-arrow { width: 42px; height: 42px; font-size: 1.4rem; }
        .iv-image-area { margin-left: -0.5rem; margin-right: -0.5rem; max-width: none !important; }
        .iv-zoom-overlay { top: 0.35rem; right: 0.35rem; }
        .iv-hint-keyboard { display: none; }
      }
    `;
    document.head.appendChild(style);
  },

  _switchEditSlide(imgKey) {
    const slides = ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
    slides.forEach(key => {
      const slide = document.getElementById(`editSlide-${key}`);
      if (slide) slide.style.display = key === imgKey ? 'block' : 'none';
    });
    this._activeEdSlide = imgKey;
    this._updateEdHeader();
    this._syncEdModeButtons();
    const modeActual = (this._markMode && this._markMode[imgKey]) || 'move';
    this._updateEdModeHint(modeActual);
  },

  _navEdSlide(direction) {
    const order = this._edSlidesOrder || ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
    const current = this._activeEdSlide || order[0];
    const idx = order.indexOf(current);
    const nextIdx = (idx + direction + order.length) % order.length;
    this._switchEditSlide(order[nextIdx]);
  },

  _zoomEdActive(direction) {
    if (!this._activeEdSlide) return;
    this._zoom(this._activeEdSlide, direction);
    this._updateEdHeader();
  },

  _updateEdHeader() {
    const titleMap = {
      'tractor-frente': 'Frontal',
      'tractor-trasera': 'Trasera',
      'tractor-lateral-izq': 'Lateral Izquierdo',
      'tractor-lateral-der': 'Lateral Derecho'
    };
    const active = this._activeEdSlide || 'tractor-frente';
    const tEl = document.getElementById('edViewTitle');
    if (tEl) tEl.textContent = titleMap[active] || active;
    const zoomLevel = (this._zoomLevels && this._zoomLevels[active]) || 1;
    const zEl = document.getElementById('edZoomLabelActive');
    if (zEl) zEl.textContent = `${Math.round(zoomLevel * 100)}%`;

    // Badge del tab "Inspección Visual": total de marcas a través de las 4 vistas.
    const total = (this._editData?.marcas_visuales || []).length;
    const tabBadge = document.getElementById('entregaTabInspBadge');
    if (tabBadge) tabBadge.textContent = total > 0 ? ` (${total})` : '';
  },

  _syncEdModeButtons() {
    const active = this._activeEdSlide;
    if (!active) return;
    const mode = (this._markMode && this._markMode[active]) || 'move';
    const btnMov = document.getElementById('edModeMov');
    const btnAdd = document.getElementById('edModeAdd');
    const btnDel = document.getElementById('edModeDel');
    if (btnMov) btnMov.classList.toggle('iv-mode-active', mode === 'move');
    if (btnAdd) btnAdd.classList.toggle('iv-mode-active', mode === 'add');
    if (btnDel) btnDel.classList.toggle('iv-mode-active', mode === 'delete');
  },

  _updateEdModeHint(mode) {
    const el = document.getElementById('edModeHint');
    if (!el) return;
    el.classList.remove('iv-mode-hint-move', 'iv-mode-hint-add', 'iv-mode-hint-delete');
    el.classList.add(`iv-mode-hint-${mode || 'move'}`);
    const hints = {
      move:   '💡 <strong>Mover:</strong> Arrastra la imagen para moverte · Toca un punto marcado para ver su foto',
      add:    '💡 <strong>Marcar:</strong> Toca un espacio para crear un punto · <em>Arrastra</em> para ajustar el tamaño',
      delete: '💡 <strong>Eliminar:</strong> toca un daño para borrarlo'
    };
    el.innerHTML = hints[mode] || hints.move;
  },

  _navViewSlide(direction) {
    const order = this._viewSlidesOrder || ['tractor-frente', 'tractor-trasera', 'tractor-lateral-izq', 'tractor-lateral-der'];
    const current = this._activeViewSlide || order[0];
    const idx = order.indexOf(current);
    const nextIdx = (idx + direction + order.length) % order.length;
    this._switchViewSlide(order[nextIdx]);
  },

  _zoomViewActive(direction) {
    const active = this._activeViewSlide || 'tractor-frente';
    this._zoom(active, direction, 'view');
    this._updateViewHeader();
  },

  _updateViewHeader() {
    const titleMap = {
      'tractor-frente': 'Frontal',
      'tractor-trasera': 'Trasera',
      'tractor-lateral-izq': 'Lateral Izquierdo',
      'tractor-lateral-der': 'Lateral Derecho'
    };
    const active = this._activeViewSlide || 'tractor-frente';
    const tEl = document.getElementById('evViewTitle');
    if (tEl) tEl.textContent = titleMap[active] || active;
    const zoomLevel = (this._zoomLevels && this._zoomLevels[active]) || 1;
    const zEl = document.getElementById('evZoomLabelActive');
    if (zEl) zEl.textContent = `${Math.round(zoomLevel * 100)}%`;
  },

  _setMarkMode(imgKey, mode) {
    if (!imgKey) return;
    if (!this._markMode) this._markMode = {};
    this._markMode[imgKey] = mode;

    if (imgKey === this._activeEdSlide) {
      this._syncEdModeButtons();
      this._updateEdModeHint(mode);
    }

    const sfxMap = { 'tractor-frente': 'Frente', 'tractor-trasera': 'Trasera', 'tractor-lateral-izq': 'LatIzq', 'tractor-lateral-der': 'LatDer' };
    const sfx = sfxMap[imgKey];
    if (!sfx) return;

    const wrap = document.getElementById(`wrap${sfx}`);
    if (wrap) {
      if (mode === 'delete') wrap.style.cursor = 'pointer';
      else if (mode === 'move') wrap.style.cursor = 'grab';
      else wrap.style.cursor = 'crosshair';
    }

    // Modo "move": touch-action auto deja al browser pan/scroll en mobile,
    // pero pointer-events queda activo para que un tap puntual sobre una
    // marca abra la foto. add/delete: bloquea el pan para no interferir con
    // drag-to-size / delete-click.
    const canvas = document.getElementById(`canvas${sfx}`);
    if (canvas) {
      if (mode === 'move') {
        canvas.style.touchAction = 'auto';
        canvas.style.pointerEvents = 'auto';
      } else {
        canvas.style.touchAction = 'none';
        canvas.style.pointerEvents = 'auto';
      }
    }
  },

  _zoom(imgKey, direction, mode) {
    if (!this._zoomLevels) this._zoomLevels = {};
    const isView = mode === 'view';

    let level = this._zoomLevels[imgKey] || 1;
    level = Math.max(1, Math.min(3, +(level + direction * 0.25).toFixed(2)));
    this._zoomLevels[imgKey] = level;

    const idMap = {
      'tractor-frente':      { img: 'imgFrente',   viewImg: 'viewImgFrente',   scroll: 'scrollFrente',   viewScroll: 'viewScrollFrente',   label: 'zoomLabelFrente',   viewLabel: 'viewZoomLabelFrente' },
      'tractor-trasera':     { img: 'imgTrasera',  viewImg: 'viewImgTrasera',  scroll: 'scrollTrasera',  viewScroll: 'viewScrollTrasera',  label: 'zoomLabelTrasera',  viewLabel: 'viewZoomLabelTrasera' },
      'tractor-lateral-izq': { img: 'imgLatIzq',   viewImg: 'viewImgLatIzq',   scroll: 'scrollLatIzq',   viewScroll: 'viewScrollLatIzq',   label: 'zoomLabelLatIzq',   viewLabel: 'viewZoomLabelLatIzq' },
      'tractor-lateral-der': { img: 'imgLatDer',    viewImg: 'viewImgLatDer',    scroll: 'scrollLatDer',    viewScroll: 'viewScrollLatDer',    label: 'zoomLabelLatDer',    viewLabel: 'viewZoomLabelLatDer' }
    };
    const ids = idMap[imgKey];
    if (!ids) return;

    const img = document.getElementById(isView ? ids.viewImg : ids.img);
    if (img) {
      if (level === 1) {
        img.style.width = '100%';
      } else {
        const nw = img.naturalWidth || img.offsetWidth;
        img.style.width = `${nw * level}px`;
      }
    }

    const scroll = document.getElementById(isView ? ids.viewScroll : ids.scroll);
    if (scroll) scroll.style.overflow = level > 1 ? 'auto' : 'hidden';

    const label = document.getElementById(isView ? ids.viewLabel : ids.label);
    if (label) label.textContent = `${Math.round(level * 100)}%`;
  },

  _drawViewMarks(canvasId, imgId, imgKey, marcas) {
    const canvas = document.getElementById(canvasId);
    const img = document.getElementById(imgId);
    if (!canvas || !img) return;

    const draw = () => {
      canvas.width = img.naturalWidth || img.offsetWidth;
      canvas.height = img.naturalHeight || img.offsetHeight;
      const ctx = canvas.getContext('2d');
      const filtered = marcas.filter(m => m.img === imgKey);
      filtered.forEach((mark, i) => {
        const px = (mark.x / 100) * canvas.width;
        const py = (mark.y / 100) * canvas.height;
        const radiusPct = (typeof mark.radius_pct === 'number' && mark.radius_pct > 0) ? mark.radius_pct : 2.5;
        const radius = Math.max(6, (radiusPct / 100) * canvas.width);

        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(220, 53, 69, 0.65)';
        ctx.fill();
        ctx.strokeStyle = '#dc3545';
        ctx.lineWidth = 2.5;
        ctx.stroke();

        if (radius >= 10) {
          ctx.fillStyle = '#fff';
          ctx.font = `bold ${Math.max(10, radius * 0.9)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(i + 1), px, py + 1);
        }
      });
    };

    if (img.complete) draw(); else img.onload = draw;
  },

  _syncAllToggles(container) {
    const toggles = (container || document).querySelectorAll('[data-selectall]');
    toggles.forEach(toggle => this._syncSelectAllToggle(toggle.dataset.selectall));
  },

  _syncSelectAllToggle(section) {
    const items = document.querySelectorAll(`[name^="${section}_"], .${section}`);
    if (items.length === 0) return;
    const allChecked = Array.from(items).every(cb => cb.checked);
    const toggle = document.querySelector(`[data-selectall="${section}"]`);
    if (toggle) toggle.checked = allChecked;
  },

  _attachCheckboxSync(container) {
    if (!container) return;
    container.addEventListener('change', (e) => {
      const cb = e.target;
      if (cb.type !== 'checkbox' || cb.dataset.selectall) return;
      // Determinar a qué sección pertenece
      const name = cb.name || '';
      const cls = cb.className || '';
      const sections = ['inspeccion', 'sistema', 'cabina', 'indicacion', 'fluido_tapon',
                        'edit-inspeccion', 'edit-sistema', 'edit-cabina', 'edit-indicacion', 'edit-fluido-tapon'];
      for (const section of sections) {
        if (name.startsWith(section + '_') || cls.includes(section)) {
          this._syncSelectAllToggle(section);
          break;
        }
      }
    });
  },

  _renderNivelBar(fluidoKey, fluidoDisplay, segmentoActivo, isReadOnly, prefix) {
    const colores = FLUIDOS_COLORES[fluidoKey] || {};
    const segments = [1, 2, 3, 4, 5, 6, 7].map(seg => {
      const isActive = segmentoActivo === seg;
      const disabledClass = isReadOnly ? 'disabled' : '';
      const activeClass = isActive ? 'active' : '';
      const colorClass = colores[seg] ? `seg-${colores[seg]}` : '';
      const onclick = isReadOnly ? '' : `onclick="window.entregaDetalleView.onSegmentClick('${prefix || fluidoKey}', ${seg})"`;
      return `<div class="nivel-segment ${colorClass} ${activeClass} ${disabledClass}" ${onclick} title="Segmento ${seg}"></div>`;
    }).join('');

    return `
      <div class="nivel-bar" data-prefix="${prefix || fluidoKey}">
        <div class="nivel-bar-title">${fluidoDisplay}</div>
        <div class="nivel-segments">
          ${segments}
        </div>
        <div class="nivel-labels">
          <span>Min</span>
          <span>Max</span>
        </div>
      </div>
    `;
  },

  onSegmentClick(prefix, segmento) {
    if (!this._fluidosNiveles) this._fluidosNiveles = {};
    // Toggle: si clickea el mismo segmento activo, lo deselecciona
    if (this._fluidosNiveles[prefix] === segmento) {
      this._fluidosNiveles[prefix] = 0;
      segmento = 0;
    } else {
      this._fluidosNiveles[prefix] = segmento;
    }

    // Extraer fluidoKey del prefix (e.g. "new_combustible" -> "combustible", "edit_fluido_combustible" -> "combustible")
    const fluidoKey = prefix.replace(/^(new_|edit_fluido_)/, '');
    const colores = FLUIDOS_COLORES[fluidoKey] || {};

    // Buscar la barra correcta por data-prefix
    const bar = document.querySelector(`.nivel-bar[data-prefix="${prefix}"]`);
    if (!bar) return;

    const segments = bar.querySelectorAll('.nivel-segment');
    segments.forEach((seg, idx) => {
      const segNum = idx + 1;
      seg.className = 'nivel-segment';
      if (colores[segNum]) seg.classList.add(`seg-${colores[segNum]}`);
      if (segNum === segmento) seg.classList.add('active');
    });

    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  _renderCheckItem(label, value) {
    return `
      <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.15rem 0;">
        <span style="color: ${value ? 'var(--success)' : 'var(--text-muted)'}; font-size: 1.1rem;">
          ${value ? '✓' : '✗'}
        </span>
        <span style="font-size: 0.9rem; text-transform: uppercase;">${label}</span>
      </div>
    `;
  }
};
